<template>
  <div class="w-full flex flex-col overflow-hidden" ref="viewer_page" style="height: 100vh;"
  >
      <!-- Fila del visor (relative): contiene el ÁREA DEL VISOR (visor + chrome) y
           el sidebar. Al fijar el panel con el pin, el sidebar pasa a hermano flex
           (340px) y el área del visor se encoge → el chrome, anclado a esa área, se
           mueve/recentra solo (sin offsets hardcodeados). -->
      <div class="flex flex-1 min-h-0 relative">

        <!-- Área del visor: el visor + TODO el chrome flotante (menú, toolbars,
             pill, zoom, help, vistas). `relative` ancla el chrome; `flex-1` lo
             encoge al acoplar el panel; `overflow-hidden` recorta. -->
        <div class="viewer-area relative flex flex-1 min-h-0 overflow-hidden">

        <!-- Estado inicial (sin archivo) — dropzone SOLO en design mode. En
             progress mode mostramos un loader mientras Entrega B inyecta la
             geometría desde el .epcb. -->
        <WidgetDropzone
          v-if="!hasFile && viewerMode === 'design'"
          :value="uploadedFile"
          @epc-file="_OnFileSelected"
          @epc-back="_HandleReturnClick"
        />

        <!-- Placeholder en progress mode mientras se descarga / decodifica
             el .epcb. Una vez Entrega B inyecte la escena, hasFile pasa a
             true y este bloque deja de mostrarse. -->
        <div v-else-if="!hasFile && viewerMode === 'progress'" class="flex-1 flex items-center justify-center p-8">
          <div class="text-center">
            <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-large"></b-icon>
            <p class="mt-2 text-sm text-gray-500">{{ $t('loading_design') }}…</p>
          </div>
        </div>

        <!-- Estado normal: viewer renderizando el cadJson (procesado si existe, sino el original) -->
        <template v-else>
          <div class="w-full flex flex-1 min-h-0">
            <CadViewer
              :cad-json="processedCadJson || cadJson"
              :fonts="fonts"
              ref="cadViewer"
              @epc-loaded="onSceneLoaded"
              @epc-pointerdown="onClick"
              @epc-viewChanged="_SyncZoomPercent"
              @epc-error="onError"
            />
          </div>
          <ViewerPanelScreenshot v-if="showPanelScreenshot"
            ref="refScreenshot"
            :screenshot="screenshot"
            @epc-close="(e) => { showPanelScreenshot = !e }"/>

          <!-- Overlay interactivo: solo no-mechanical. Genera hitboxes por
               segmento de polilínea para capturar clicks sin tocar el render
               principal del Viewer.
               Pasamos `$refs.cadViewer` directo (no a través de data) para evitar
               que Vue intente observar reactivamente el árbol THREE.js entero. -->
          <DxfInteractiveOverlay
            v-if="advanceOverlayEnabled && viewerReady"
            ref="refDxfOverlay"
            :viewer="$refs.cadViewer"
            :cad-json="processedCadJson || cadJson"
            :filter="_AdvanceOverlayFilter"
            :interactive="!isDraggable"
            @epc-entity-click="_OnEntityClickForInfo"
            @epc-entity-dblclick="_OnEntityClickForAdvance"
          />
        </template>

        <!-- ───────── Chrome flotante (anclado al área del visor) ───────── -->

        <!-- Menú ☰ arriba-izquierda: selector de vistas (estilo Excalidraw). -->
        <WidgetMenu
          v-if="hasProcessed"
          :current-view="displayView"
          :views="menuViews"
          @epc-open-panel="_OnMenuViewSelected"
        />

        <!-- Indicador/selector flotante de la capa activa (solo progress). Al
             pulsarlo abre un dropdown con las capas VISIBLES (menos la "0") para
             cambiar de capa activa al vuelo. El panel lateral queda para ocultar
             capas / ver detalle. El pill se acomoda al texto (tope 70%). -->
        <div
          v-if="isProgressMode && hasFile"
          class="active-layer-pill absolute bottom-3 left-1/2 -translate-x-1/2 z-[6] max-w-[70%]"
        >
          <WidgetSelectDropdown
            :value="activeLayerCode"
            :options="activeLayerOptions"
            :empty-text="$t('no_layers_to_display')"
            position="is-top-left"
            @epc-select="_SelectActiveLayer"
            @epc-active-change="_OnLayerSwitcherChange"
          >
            <template #trigger="{ active }">
              <div
                class="inline-flex items-center gap-1.5 max-w-full px-3 py-1.5 rounded-full
                       text-[13px] text-gray-600 bg-white border border-[#e9e9ef] shadow-md
                       cursor-pointer select-none transition-colors
                       hover:bg-[#f2f2f7] hover:border-[#c9d6ef]"
              >
                <b-icon pack="fas" icon="layer-group" size="is-small" class="text-[#3a6bc8]" />
                <span class="min-w-0 truncate">
                  <template v-if="activeLayerCode">{{ $t('you_are_on_layer') }}: <strong class="text-[#3a6bc8] font-semibold">{{ activeLayerName }}</strong></template>
                  <template v-else>{{ $t('all_layers') }}</template>
                </span>
                <b-icon pack="fas" icon="chevron-down" size="is-small"
                  class="text-gray-400 transition-transform"
                  :class="active ? 'rotate-180' : ''" />
              </div>
            </template>
          </WidgetSelectDropdown>
        </div>

        <!-- Toolbar central (arriba-centro). -->
        <Toolbar
          v-if="hasFile"
          :buttons="toolbarButtons"
          :context="toolbarContext"
          variant="floating"
          tooltip-position="is-bottom"
          @action="_OnToolbarAction"
        />

        <!-- Acciones flotantes arriba-derecha (upload / volver). Al fijar el panel
             el botón de panel-toggle desaparece → quitamos el hueco reservado. -->
        <Toolbar
          v-if="hasFile"
          :buttons="rightActionButtons"
          :context="subheaderContext"
          variant="floating"
          align="right"
          separated
          tooltip-position="is-bottom"
          :class="{ 'epc-toolbar--no-toggle-gap': sidebarPinned }"
          @action="_OnSubheaderAction"
        />

        <!-- Botón de panel lateral arriba-derecha. Oculto cuando está fijado. -->
        <WidgetPanelToggle
          v-if="hasFile"
          :active="showLayerPanel"
          :pinned="sidebarPinned"
          @epc-toggle="showLayerPanel = !showLayerPanel"
        />

        <!-- Control de zoom flotante (abajo-izquierda). -->
        <WidgetZoom
          v-if="hasFile"
          :zoom-percent="zoomPercent"
          @epc-click-zoom-in="_OnClickZoom"
          @epc-click-zoom-out="_OnClickZoom"
          @epc-click-fit="_OnFitContent"
        />

        <!-- Botón de ayuda flotante (abajo-derecha). -->
        <WidgetHelp
          v-if="hasFile"
          @epc-click-help="_OnClickHelp"
        />

        <!-- Panel de Vistas (izquierda, slide-in). -->
        <ViewerPanelViews
          :visible="showPanelViews"
          :views="viewsList"
          :active-view="displayView"
          @view-selected="_OnViewSelected"
          @epc-close="showPanelViews = false"
        />

        </div><!-- /viewer-area -->

        <!-- Panel lateral compartido (chrome estilo Excalidraw). Hermano del área
             del visor: flotante (overlay) por defecto, o acoplado (flex 340px,
             encoge el área) al fijarlo con el pin. La pestaña "Capas" muestra la
             lista de capas de la vista activa, abierta por defecto. -->
        <WidgetSidebar
          v-if="showLayerPanel"
          :tabs="sidebarTabs"
          :header-actions="sidebarHeaderActions"
          pinnable
          :docked="sidebarPinned"
          @epc-action="_OnSidebarAction"
          @epc-pin="sidebarPinned = $event"
          @epc-close="_CloseLayersSidebar"
        >
          <template #layers>
            <div class="h-full">
              <LayersList
                :layers="filterLayers"
                :active-name="activeLayerCode"
                :locked="!isProgressMode || !progressViewerConfig.allowLayerSwitch"
                :actions-always-visible="sidebarPinned"
                @toggleLayer="_OnToggleLayer"
                @activate-layer="_OnActivateLayer"
                @layer-info="_OnLayerInfo"
              />
            </div>
          </template>
        </WidgetSidebar>

      </div>

      <!-- Input invisible que dispara el botón "Subir archivo" del header -->
      <input
        ref="fileInputHidden"
        type="file"
        accept=".dxf,.dwg"
        class="hidden"
        @change="_OnHiddenFileInputChange"
      />

      <!-- Modal de selección de capas para electrical/trenches. Se abre al
           pulsar el botón "Procesar" del Toolbar. Al confirmar (apply) emite
           las capas seleccionadas (con nombre editable) y encadenamos con
           SavePictogramModal — mismo patrón que "Configurar → Guardar" del
           mecánico. La lista de capas se pasa por `show(layers)`, no por prop. -->
      <SelectLayersModal
        ref="refSelectLayersModal"
        @epc-apply="_OnSelectLayersApply"
      />

      <!-- Modal imperativo que aloja el diálogo de ayuda (HelpDialog). -->
      <epc-modal-wrapper ref="helpModal" />

      <!-- Modal imperativo con el detalle de una capa (LayerInfoModal). -->
      <epc-modal-wrapper ref="layerInfoModal" />

      <!-- Modal imperativo de confirmación (reset/limpiar, etc.). -->
      <epc-modal-wrapper ref="confirmModal" />

      <ConfigureSubdivisionsModal
        ref="refConfigureSubdivisionsModal"
        @epc-apply="_OnConfigureSubdivisionsApply" />

      <SavePictogramModal
        ref="refSavePictogramModal"
        @epc-apply="_OnSavePictogramApply" />

      <SaveSuccessModal
        ref="refSaveSuccessModal"
        @epc-close="_OnSaveSuccessClose"
        @epc-new="_OnSaveSuccessNew"
        @epc-back="_OnSaveSuccessBack" />

      <ViewerSpinnerLoader ref="refSpinnerLoader" :is-loading="isLoading" :custom-loader-text="customLoaderText"/>

      <!-- Modal de avance lineal — se abre al clickear una polilínea con ancho
           en viewerMode='progress'. RegisterAdvanceModal mantiene el estado
           UI internamente; al Aplicar emite epc-apply con { direction, meters,
           status } y el LayoutBuilder los procesa en _OnAdvanceApply. -->
      <RegisterAdvanceModal
        ref="refRegisterAdvanceModal"
        @epc-apply="_OnAdvanceApply" />

      <!-- Popover de INFO (un click en el visor): campos extra + certificación
           por metros + progreso. El doble click abre el modal de avance. -->
      <ElectricalInfoPopover
        v-if="infoPopover.visible"
        :position="infoPopover.position"
        :data="infoPopover.data"
        :loading="infoPopover.loading"
        :segment-points="infoPopover.segmentPoints"
        :anchor="infoPopover.anchor"
        @close="_CloseInfoPopover" />

      <!-- Tooltip flotante con el detalle del avance al pasar el mouse por una
           polilínea pintada. El widget es genérico; aquí mapeamos el breakdown
           del avance a sus filas (hoverTooltipRows). -->
      <WidgetHoverTooltip
        :visible="advanceTooltip.visible"
        :x="advanceTooltip.x"
        :y="advanceTooltip.y"
        :title="$t('linear_advance')"
        :rows="hoverTooltipRows"
        :note="advanceTooltip.breakdown.length === 0 ? $t('no_progress_registered') : ''"
      />

      <!-- Overlay SVG (pantalla completa, sin capturar eventos) del segmento armado:
           flechas de sentido + visualización del arrastre (recorrido punteado, punto
           de inicio gris y punta azul móvil, guía punteada hacia el badge). -->
      <svg
        v-if="inlineArmed.visible"
        class="fixed inset-0 w-screen h-screen pointer-events-none z-[54]"
      >
        <polygon
          v-for="(arrow, i) in inlineArmed.arrows"
          :key="i"
          :points="_ArrowPoints(arrow)"
          fill="#3a6bc8"
        />
        <template v-if="inlineDrag.visible">
          <polyline :points="inlineDrag.dash" fill="none" stroke="#3a6bc8" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round" />
          <polyline :points="inlineDrag.leader" fill="none" stroke="#9ca3af" stroke-width="1.5" stroke-dasharray="3 3" />
          <circle :cx="inlineDrag.start.x" :cy="inlineDrag.start.y" r="4" fill="#9ca3af" stroke="#fff" stroke-width="1.5" />
          <circle :cx="inlineDrag.tip.x" :cy="inlineDrag.tip.y" r="5" fill="#3a6bc8" stroke="#fff" stroke-width="2" />
        </template>
      </svg>

      <!-- Badge de metros del arrastre, a un costado del trazo (offset perpendicular). -->
      <div
        v-if="inlineDrag.visible"
        class="fixed z-[56] pointer-events-none px-2 py-0.5 rounded-md text-xs font-semibold text-white bg-[#3a6bc8] shadow-md -translate-x-1/2 -translate-y-1/2"
        :style="{ left: inlineDrag.badge.x + 'px', top: inlineDrag.badge.y + 'px' }"
      >{{ inlineDrag.badge.text }}</div>

      <!-- Marcadores A/B SOLO visuales (pointer-events:none) para no robar el
           pointerdown del arrastre; la elección del inicio la resuelve el gesto
           (click junto a un extremo = ese extremo por cercanía). -->
      <template v-if="inlineArmed.visible">
        <div
          class="fixed z-[55] pointer-events-none -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shadow-md"
          :class="_InlineMarkerClass('l2r')"
          :style="{ left: inlineArmed.a.x + 'px', top: inlineArmed.a.y + 'px' }"
        >A</div>
        <div
          class="fixed z-[55] pointer-events-none -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shadow-md"
          :class="_InlineMarkerClass('r2l')"
          :style="{ left: inlineArmed.b.x + 'px', top: inlineArmed.b.y + 'px' }"
        >B</div>
        <!-- Punto de continuación: frente del avance ya ejecutado (desde donde sigue).
             Se oculta durante el arrastre (lo reemplaza el inicio gris de inlineDrag). -->
        <div
          v-if="inlineArmed.continue.visible && !inlineDrag.visible"
          class="fixed z-[55] pointer-events-none -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-[#3a6bc8] border-2 border-white shadow"
          :style="{ left: inlineArmed.continue.x + 'px', top: inlineArmed.continue.y + 'px' }"
        ></div>
      </template>

      <!-- Pista guía del gesto (mientras el segmento está armado y aún no se marca). -->
      <div
        v-if="inlineArmed.visible && !inlineDrag.visible && !inlinePopover.visible"
        class="fixed top-20 left-1/2 -translate-x-1/2 z-[55] pointer-events-none px-3 py-1.5 rounded-full text-white text-xs font-medium shadow-lg flex items-center gap-1.5"
        :class="inlineArmed.completed ? 'bg-[#0e9f6e]' : 'bg-[#3a6bc8]'"
      >
        <b-icon v-if="inlineArmed.completed" pack="fas" icon="circle-check" size="is-small" />
        <b-icon v-else-if="inlineArmed.locked" pack="fas" icon="lock" size="is-small" />
        <span>{{ inlineArmedHint }}</span>
      </div>

      <!-- Popover de estado + fecha del gesto de avance inline (al soltar). -->
      <InlineAdvancePopover
        :visible="inlinePopover.visible"
        :position="inlinePopover.position"
        :statuses="inlinePopover.statuses"
        :direction="inlinePopover.direction"
        :delta-meters="inlinePopover.deltaMeters"
        :total-meters="inlinePopover.totalMeters"
        :total-length="inlinePopover.totalLength"
        :preselect="inlinePopover.preselect"
        :loading="inlinePopover.loading"
        :error-msg="inlinePopover.errorMsg"
        @epc-apply="_OnInlinePopoverApply"
        @epc-cancel="_OnInlinePopoverCancel"
      />
    </div>
</template>

<script>
import CadViewer from "../engine/CadViewer"
// imports modales
import ConfigureSubdivisionsModal from "./components/modals/ConfigureSubdivisionsModal.vue"
import SavePictogramModal from "./components/modals/SavePictogramModal.vue"
import SaveSuccessModal from "./components/modals/SaveSuccessModal.vue"
// imports componentes
// servicios y helpers de guardado
import { savePictogram, uploadNewPictogramVersion } from "./services/pictogram.service"
import { processSubdivisions } from "./services/process-subdivisions.service"
import { captureViewerByViews, captureViewerSnapshot } from "./helpers/screenshot.helper"

import Vue from "vue"
import { FONTS } from "./constants/fonts"
import LayersList from "@pictograms/shared/components/panels/LayersList.vue"
import Subheader from "@pictograms/shared/Subheader.vue"
import ViewerPanelViews from "@pictograms/shared/components/panels/ViewerPanelViews.vue"
import ViewerSpinnerLoader from "../viewer-konva/ViewerSpinnerLoader.vue"
import ViewerPanelScreenshot from "../viewer-konva/ViewerPanelScreenshot.vue"

import { createLogger } from "../utils/logger"
const logger = createLogger('CadStudio')

import Toolbar from "@pictograms/shared/Toolbar.vue"

import { getLayerDescriptors } from "./helpers/layer-descriptors.helper"
import { getTextLayers, computeViewVisibility } from "./helpers/view-filter.helper"
import { buildFilterLayers } from "./helpers/filter-layers.helper"
import { computeRobustBounds } from "./helpers/robust-bounds.helper"
import { loadAdvanceState, persistAdvanceState } from "./helpers/advance-storage.helper"
import { getEntityMaxWidth, getEntityTotalLength } from "./helpers/entity-geometry.helper"
import { simulatePools, upgradeAdvanceState } from "./helpers/advance-pool.helper"
import { computeStatusEligibility, computeAdvanceTarget } from "./helpers/advance-eligibility.helper"
import { computeHoverBreakdown } from "./helpers/hover-tooltip.helper"
import { AdvanceOverlay } from "./rendering/AdvanceOverlay"
import { HighlightOverlay } from "./rendering/HighlightOverlay"
import { HoverTooltipController } from "./rendering/HoverTooltipController"
import { InlineAdvanceController } from "./rendering/InlineAdvanceController"
import {
  buildToolbarButtons, buildToolbarContext,
  buildSubheaderItems, buildSubheaderContext,
} from "./helpers/menu-schemas.helper"
import { VIEW_CODE, DEFAULT_VIEW } from "./constants/view-code"
import { isFullScreenActive, requestFullScreen, exitFullScreen } from "@pictograms/shared/helpers/ui.helpers"
import { parseCadFile } from "./services/cad-file.service"
import { getProfile } from "./profiles"
import DxfInteractiveOverlay from "../components/DxfInteractiveOverlay.vue"
import { computeLayerMetrics } from "./helpers/dxf-layer-metrics.helper"
import { encodeScene } from "./helpers/binary-scene.encoder"
import { decodeScene } from "./helpers/binary-scene.decoder"
import { registerLayerProgress, getPictogramComponents } from "../services/pictograms.service"
import { uploadFile } from "@pictograms/shared/services/uppy-client.service"
import SelectLayersModal from "./components/modals/SelectLayersModal.vue"
import LayerInfoModal from "./components/LayerInfoModal.vue"
import RegisterAdvanceModal from "./components/modals/RegisterAdvanceModal.vue"
import ElectricalInfoPopover from "./components/ElectricalInfoPopover.vue"
import InlineAdvancePopover from "./components/InlineAdvancePopover.vue"
import { WidgetZoom, WidgetMenu, WidgetDropzone, WidgetSidebar, WidgetHelp, WidgetPanelToggle, WidgetHoverTooltip, WidgetSelectDropdown } from "@pictograms/shared/widgets"
import HelpDialog from "@pictograms/shared/components/panels/HelpDialog.vue"
import { WidgetDialog } from "@pictograms/shared/widgets"
import EpcModalWrapper from "·/components/epc-modal-wrapper.vue"
import { getElectricalComponentInfo } from "../api/pictograms.api.js"

// Estados posibles del avance lineal — color es hex Three.js, no CSS.
// El orden determina el que se muestra primero en el selector.
const ADVANCE_STATUSES = [
  { value: 'pending',     color: 0xfdcb6e }, // amarillo
  { value: 'in_progress', color: 0x00b894 }, // verde (default)
  { value: 'blocked',     color: 0xe17055 }, // rojo
  { value: 'completed',   color: 0x0984e3 }, // azul
]
const ADVANCE_DEFAULT_STATUS = 'in_progress'

// Colores uniformes del resaltado del footer (dropdown "Resaltar"). Un color
// para los elementos SIN avance registrado (pendientes) y otro para los que
// tienen avance. Son hex Three.js (int), no CSS.
const HIGHLIGHT_COLORS = {
  without: 0xff2d2d, // rojo chillante — pendientes (sin avance)
  with:    0x1a8a1c, // verde oscuro — con avance
}


// Atenuado de capas NO-activas (progress mode): en vez de bajar opacidad, las
// pintamos de un gris plano y neutro (sin color) para que la capa activa resalte.
// Es el gris de la GEOMETRÍA (0..1 por canal). El gris de los CARRILES de avance
// vive ahora en AdvanceOverlay (DIM_ADVANCE_*).
const DIM_LAYER_GRAY      = 0.72

// Color de la lane transitoria del gesto de avance inline (azul de marca).
const INLINE_PREVIEW_COLOR = 0x3a6bc8

export default {
  name: "CadStudio",
  components: {
    CadViewer,

    ConfigureSubdivisionsModal,
    SavePictogramModal,
    SaveSuccessModal,

    LayersList,
    ViewerPanelViews,
    ViewerSpinnerLoader,
    ViewerPanelScreenshot,

    Subheader,
    Toolbar,
    WidgetZoom,
    WidgetMenu,
    WidgetDropzone,
    WidgetSidebar,
    WidgetHelp,
    WidgetPanelToggle,
    WidgetHoverTooltip,
    WidgetSelectDropdown,
    EpcModalWrapper,

    DxfInteractiveOverlay,
    SelectLayersModal,
    RegisterAdvanceModal,
    ElectricalInfoPopover,
    InlineAdvancePopover,
  },

  props: {
    pictogram: {
      type: Object,
      default() { return {} },
    },
    typePictogram: { type: String, default: 'mechanical_installation' }, // si es null es pictograma de parque fotovoltaico sino de evacuacion line
    workLayer: { type: String, default: null },
    canActionsPictogram: { type:Object, default() { return {} } }, // acciones que se puede realizar sobre el pictograma por defecto ninguna
    entityId: { type: [Number, String], required: true },
    entityType: { type: String, default: 'project' },
    /**
     * Modo de operación:
     *   - 'create' (default): flujo normal — al guardar abre SavePictogramModal y crea pictograma nuevo
     *   - 'upload-new-version': flujo de actualizar — al guardar dispara upload + redirect a /process con query
     */
     mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'upload-new-version'].includes(v),
    },

    /**
     * Eje ortogonal a `mode`: qué FAMILIA de features expone el visor.
     *   - 'design'  (default): admin carga/procesa/guarda el diseño. NO muestra
     *     overlay de avance ni tooltip ni modal de avance lineal.
     *   - 'progress': lado cliente — el usuario registra avance sobre las
     *     polilíneas con width. Activa DxfInteractiveOverlay, modal de avance,
     *     hover tooltip y rehidratado desde localStorage.
     * Ambas familias se pueden seguir desarrollando en paralelo sin romper
     * el modo opuesto — todo el código de avance se conserva intacto y solo
     * se gatea por este flag.
     */
    viewerMode: {
      type: String,
      default: 'design',
      validator: (v) => ['design', 'progress'].includes(v),
    },
  },

  data() {
    return {
      filterLayers: [],
      // Fuente de verdad de la visibilidad de cada capa (name → bool) en la
      // capa app. El motor (DxfViewer) solo ejecuta ShowLayer; no guarda estado
      // de visibilidad. La siembra _ApplyDisplayView (base por vista) y la muta
      // el toggle manual; _RefreshFilterLayers la lee para pintar el panel.
      layerVisibility: {},
      showLayerPanel: false,
      sidebarPinned: false, // panel lateral fijado (acoplado): encoge el visor

      // Modo PAN (herramienta mano). Por defecto OFF → modo interacción: clicks
      // (info/avance) y tooltip de hover activos, y el arrastre NO mueve el lienzo
      // (así un click no pelea con el pan). Al activarlo se invierte: mano para
      // mover (cursor grab) y se apagan clicks + tooltip. El zoom (rueda) va en
      // ambos modos.
      isDraggable: false,

      zoomPercent: 100, // % de zoom mostrado en el control flotante (100 = fit)

      showPanelViews: false,
      displayView: DEFAULT_VIEW,   // código de la vista activa (pilings|structures|modules|panels). 'structures' es el default.
      showPanelScreenshot: false, // bandera que indica si se muestra o no el panel de screenshots

      screenshot: null, // base64 preview del último PNG capturado


      isLoading: false, // Indica si esta visible el loader
      customLoaderText: null, // texto mostrado en el loader

      // Archivo CAD cargado por el usuario. Mientras es null, la vista
      // muestra el dropzone; cuando se setea, se renderiza el viewer.
      uploadedFile: null,
      cadJson: null,           // resultado del parseo
      isParsing: false,
      parseError: null,

      // Configuración aplicada desde ConfigureSubdivisionsModal (la usará D4 para procesar)
      cadLayeredDesign: null,
      createElements: { pilings: false, modules: false },

      processedCadJson: null,        // cadJson tras pasar por el worker (con subdivisiones)
      totalEntitiesDesign: { pilings: 0, modules: 0, panels: 0 },       // totales devueltos por el worker

      // Avance lineal (solo no-mechanical). Flag que indica que el Viewer
      // ya está montado y su DxfViewer/escena Three.js están disponibles —
      // en cuanto pasa a true, el template pasa `$refs.cadViewer` al overlay.
      //
      // IMPORTANTE: NO guardamos `$refs.cadViewer` ni el cad entity (que tiene
      // vertices) en data(). Vue 2 los observaría recursivamente y para un DXF
      // grande eso cuelga el thread durante segundos. Lo que necesitamos no
      // reactivo se inicializa en created() como propiedad de instancia.
      viewerReady: false,
      // El estado del modal de avance lineal (direction/meters/status) vive
      // dentro de RegisterAdvanceModal.vue. Acá solo guardamos el resultado
      // tras el apply, vía _OnAdvanceApply → _advanceState (instance prop).
      //
      // Tooltip mostrado al hacer hover sobre una polilínea con avance pintado.
      // Se posiciona con position:fixed usando coords del mouse (clientX/Y).
      advanceTooltip: {
        visible:     false,
        x:           0,
        y:           0,
        totalMeters: 0,
        totalLength: 0,
        // Acumulado por status, derivado de _advanceState[handle].segments.
        // Cada entry: { value, label, color, meters }.
        breakdown:   [],
      },
      // Visualización del arrastre: punto de inicio (gris), recorrido punteado,
      // tip (azul, se mueve) y badge de metros a un costado con guía punteada.
      // Todo en coords de pantalla, reproyectado en cada frame del gesto.
      inlineDrag: {
        visible: false,
        start:   { x: 0, y: 0 },   // inicio del tramo marcado (frente de avance)
        tip:     { x: 0, y: 0 },   // punta móvil (fracción actual)
        dash:    '',               // polyline del tramo marcado (punteado)
        leader:  '',               // guía punteada tip → badge
        badge:   { x: 0, y: 0, text: '' },
      },
      // Toggle del MODO "Avance en línea" (toolbar). OFF (default): el doble-click
      // abre el modal de registro (fallback). ON: el doble-click arma el gesto inline.
      inlineAdvanceActive: false,
      // Handle del segmento con el registro de avance inline ARMADO (doble-click).
      // null = sin armar (el gesto no captura). Scope del InlineAdvanceController.
      inlineArmedHandle: null,
      // Marcadores A/B del segmento armado (coords de pantalla, re-proyectadas por
      // rAF para seguir pan/zoom). `selected` resalta el extremo de inicio elegido.
      inlineArmed: {
        visible:     false,
        a:           { x: 0, y: 0 },
        b:           { x: 0, y: 0 },
        selected:    null,   // 'l2r' (A) | 'r2l' (B)
        arrows:      [],     // chevrons de sentido [{x,y,angle}] en coords de pantalla
        locked:      false,  // sentido ya fijado (hubo avance) → sin cambiar origen
        completed:   false,  // no queda estado por avanzar (todos los niveles al 100%)
        appliedFrac: 0,      // fracción ya avanzada (0..1) → punto de continuación
        continue:    { visible: false, x: 0, y: 0 },
      },
      // Popover de estado + fecha del gesto inline (al soltar el arrastre).
      inlinePopover: {
        visible:     false,
        position:    { x: 0, y: 0 },
        statuses:    [],
        direction:   'l2r',
        deltaMeters: 0,
        totalMeters: 0,
        totalLength: 0,
        preselect:   null,
        loading:     false,
        errorMsg:    '',
      },
      // Popover de INFO (un click): extra_attributes + certificación + progreso.
      // El doble click abre el modal de avance (RegisterAdvanceModal).
      infoPopover: {
        visible:  false,
        position: { x: 0, y: 0 },
        data:     null,
        loading:  false,
        // Contorno del segmento proyectado a pantalla + punto ancla del leader
        // (se recalculan por rAF mientras el popover está abierto → siguen al
        // segmento en pan/zoom).
        segmentPoints: [],
        anchor:        null,
      },
      advanceStatuses: ADVANCE_STATUSES.map(s => s.value),

      // Capas para el SelectLayersModal (solo no-mechanical). Se rellena lazy
      // al abrir el modal (`_BuildLayersList`). Por defecto todas las capas
      // arrancan seleccionadas — el usuario destildara las que no quiera
      // persistir en pictogram_layers.
      dxfLayersInfo:      [], // [{name, displayName, color, entityCount, lengthMeters}]
      selectedLayerCodes: [], // [string] subset de dxfLayersInfo[].name
      // Nombres editados por el usuario en SelectLayersModal: { [originalName]: customName }.
      // Sólo presentes las capas renombradas — el resto cae a displayName del DxfViewer.
      customLayerNames:   {},
      // Capa activa en el panel "Capas" (solo cliente / progress mode).
      // Si tiene un code (electrical_n / trench_n), únicamente las
      // polilíneas de esa capa responden a click. Si es null, todas las
      // capas trackeables son clickeables (comportamiento default).
      activeLayerCode:    null,
      // Modo de resaltado activo del dropdown "Resaltar" del footer:
      //   null      → sin resaltado
      //   'without' → resaltados los elementos SIN avance registrado
      //   'with'    → resaltados los elementos CON avance registrado
      highlightMode:      null,
      // Modo de visualización de estados sobre cada elemento:
      //   'last'         → solo el último estado registrado (default)
      //   'all'          → todos los estados (carriles paralelos)
      //   <statusValue>  → solo ese estado puntual
      statusViewMode:     'last',
    }
  },

  computed: {
    /** True cuando el usuario ya cargó un archivo CAD. Controla la vista inicial vs viewer. */
    hasFile() {
      return !!this.cadJson
    },

    /** True cuando el worker ya generó las subdivisiones (modal aceptado en D4). */
    hasProcessed() {
      return !!this.processedCadJson
    },

    /**
     * Lista de vistas para el panel "Vistas".
     * Solo tiene sentido tras procesar — antes está vacía y el botón del subheader está disabled.
     * "Estructuras" es la vista por defecto al procesar; no hay vista "Todas".
     * "Paneles" se excluye porque son demasiado detallados para usarse como vista filtrada.
     */
    viewsList() {
      if (!this.hasProcessed) return []
      return this._GetLayers()
        .filter(l => l.code !== 'panels')
        .map(l => ({
          code: l.code,
          name: this.$t(l.code),
          icon: l.icon,
        }))
    },

    /** Vistas para el menú ☰ (WidgetMenu). labelKey = code → $t(code). */
    menuViews() {
      return this.viewsList.map(v => ({ code: v.code, icon: v.icon, labelKey: v.code }))
    },

    /**
     * Set de nombres de layers que contienen TEXT o MTEXT.
     * Escanea tres lugares porque los textos en DXF pueden vivir en:
     *   1. Top-level entities (caso clásico)
     *   2. Dentro de blocks (definiciones reusables) — sus entities tienen su propio layer
     *   3. Layers de los INSERT que referencian esos blocks (porque al instanciar el block,
     *      el INSERT aporta su layer y las entities con layer="0" lo heredan)
     */
    textLayers() {
      return getTextLayers(this.processedCadJson || this.cadJson)
    },

    /**
     * Total de estructuras detectadas en el DXF cargado (POLYLINE/LWPOLYLINE).
     * Lo usa _GetLayers() para el descriptor del layer "structures" que se pasa al worker.
     */
    total_structures() {
      return (this.cadJson?.entities || []).filter(
        e => e.type === 'POLYLINE' || e.type === 'LWPOLYLINE'
      ).length
    },

    /** Schema declarativo del Toolbar (delega al helper menu-schemas). */
    toolbarButtons() {
      return buildToolbarButtons()
    },

    /** Estado del Toolbar para resolver active/visible/disabled de cada item. */
    toolbarContext() {
      return buildToolbarContext({
        pictogram:      this.pictogram,
        showLayerPanel: this.showLayerPanel,
        isDraggable:    this.isDraggable,
        allowSave:      this._AllowedAction('save'),
        typePictogram:  this.typePictogram,
        loadFlow:       this.profile.design.loadFlow,
        linearAdvance:  this.profile.progress.linearAdvance,
        viewerMode:     this.viewerMode,
        features:       {
          showHighlightMenu: this.progressViewerConfig.showHighlightMenu,
          showStatusFilter:  this.progressViewerConfig.showStatusFilter,
        },
        highlightMode:       this.highlightMode,
        statusViewMode:      this.statusViewMode,
        activeLayerStatuses: this.activeLayerStatuses,
        inlineAdvance:       this.profile.progress.inlineAdvance,
        inlineAdvanceActive: this.inlineAdvanceActive,
      })
    },

    /**
     * Estados de la capa activa (para las opciones dinámicas del dropdown
     * "Ver avance"). Formato uniforme { value, label, color } vía
     * _GetStatusesForLayer. Si no hay capa activa, no ofrecemos estados
     * puntuales (solo "último" y "todos").
     */
    activeLayerStatuses() {
      const code = this.activeLayerCode
      if (!code) return []
      const pl = (this.pictogram && this.pictogram.layers || []).find(l => l.code === code)
      if (!pl) return []
      return this._GetStatusesForLayer(pl)
    },

    /**
     * Configuración del visor de avance (progress mode), pensada para ser
     * editable por el administrador desde `pictogram.metadata.progress_viewer`.
     * Ambas features vienen ACTIVAS por defecto; el admin las desactiva
     * escribiendo `false` en la clave correspondiente. Se deja preparado el
     * canal (metadata) — cuando exista la UI de admin solo hay que persistir
     * esas claves; el frontend ya las respeta.
     *
     *   - allowLayerSwitch:  permite cambiar de capa activa dentro del mismo
     *     modo de registro (panel de Capas desbloqueado).
     *   - showHighlightMenu: muestra el dropdown "Resaltar" en el footer.
     *   - showStatusFilter:  muestra el dropdown "Ver avance" (qué estados se
     *     visualizan sobre cada elemento) en el footer.
     */
    progressViewerConfig() {
      const cfg = (this.pictogram && this.pictogram.metadata && this.pictogram.metadata.progress_viewer) || {}
      return {
        allowLayerSwitch:  cfg.allow_layer_switch  !== false,
        showHighlightMenu: cfg.show_highlight_menu !== false,
        showStatusFilter:  cfg.show_status_filter  !== false,
      }
    },

    /** Schema declarativo del Subheader (cambia según haya o no archivo). */
    subheaderItems() {
      return buildSubheaderItems({ hasFile: this.hasFile })
    },

    /** Estado del Subheader para resolver active/disabled de cada item. */
    subheaderContext() {
      return buildSubheaderContext({
        hasProcessed:   this.hasProcessed,
        showPanelViews: this.showPanelViews,
        showLayerPanel: this.showLayerPanel,
        viewerMode:     this.viewerMode,
      })
    },

    /** Acciones flotantes arriba-derecha (estilo Excalidraw): los items
     *  side:'right' del schema del subheader (upload, volver), reusados tal
     *  cual. 'volver' al final, como en solar-park. */
    rightActionButtons() {
      const right = this.subheaderItems.filter(b => b.side === 'right')
      const back = right.filter(b => b.id === 'back')
      const others = right.filter(b => b.id !== 'back')
      return [...others, ...back]
    },

    /** True cuando el LayoutBuilder se usa como visor de avance del cliente. */
    isProgressMode() {
      return this.viewerMode === 'progress'
    },

    /**
     * Perfil de visor del tipo actual (studio/profiles). Declara el comportamiento
     * por modo (design/progress): flujo de carga, captura, resumen, avance lineal…
     * SCAFFOLD: expuesto para que las decisiones por tipo migren de if/else a
     * `this.profile[this.viewerMode].*` paso a paso. Aún no se consume.
     */
    profile() {
      return getProfile(this.typePictogram)
    },

    /**
     * Nombre humano de la capa activa (para el indicador flotante). Resuelve
     * el `name` del pictogram_layer por su code; si no hay capa activa,
     * devuelve el rótulo "todas las capas".
     */
    activeLayerName() {
      const code = this.activeLayerCode
      if (!code) return this.$t('all_layers')
      const pl = (this.pictogram && this.pictogram.layers || []).find(l => l.code === code)
      return (pl && pl.name) || code
    },

    /**
     * El overlay interactivo para pintar avance lineal aplica solo en:
     *   - tipos no-mechanical (electrical_installation, electrical_trenches, …)
     *   - viewerMode === 'progress' (lado cliente)
     * En 'design' (admin) está deshabilitado — el admin solo carga el diseño,
     * el registro de avance vive en el viewer cliente.
     */
    advanceOverlayEnabled() {
      return this.hasFile
        && this.profile.progress.linearAdvance
        && this.isProgressMode
    },

    /**
     * Gesto de avance INLINE sobre el canvas (click inicio A/B + arrastre delta +
     * popover), opt-in por perfil (profile.progress.inlineAdvance). Mientras esté
     * en false se mantiene el flujo doble-click → RegisterAdvanceModal. Cuando está
     * activo, el overlay deja de capturar clicks (el controller del gesto los maneja).
     */
    inlineAdvanceEnabled() {
      return this.advanceOverlayEnabled
        && this.viewerReady
        && !!this.profile.progress.inlineAdvance
    },

    /** Texto de la pista guía del gesto, según estado (completado / bloqueado / inicial). */
    inlineArmedHint() {
      if (this.inlineArmed.completed) return this.$t('inline_advance_completed')
      return this.inlineArmed.locked
        ? this.$t('inline_advance_hint_locked')
        : this.$t('inline_advance_hint')
    },

    /**
     * Lista que recibe el SelectLayersModal. Combina la info estructural
     * (dxfLayersInfo) con el flag `selected` derivado de selectedLayerCodes
     * para mantener una sola fuente de verdad sobre la selección.
     */
    dxfLayersForModal() {
      const sel = new Set(this.selectedLayerCodes)
      return this.dxfLayersInfo.map(l => ({
        ...l,
        selected: sel.has(l.name),
      }))
    },

    /** Pestañas del WidgetSidebar. De momento solo "Capas"; extensible. */
    sidebarTabs() {
      return [
        { key: 'layers', icon: 'layer-group', label: this.$t('layers') },
      ]
    },

    /**
     * Acciones de cabecera del WidgetSidebar (antes del pin). En la pestaña de
     * capas: ocultar/ver todas — el icono alterna eye / eye-slash según el estado.
     */
    sidebarHeaderActions() {
      return [
        {
          key:     'toggle-all-layers',
          icon:    this.allLayersVisible ? 'eye' : 'eye-slash',
          tooltip: this.$t('toggle_all_layers'),
        },
      ]
    },

    /**
     * Estado del "toggle todas" del panel de capas. Derivado de filterLayers
     * para estar siempre en sync con la visibilidad real (misma lógica que
     * tenía LayersPanel internamente).
     */
    allLayersVisible() {
      const list = this.filterLayers || []
      return list.length > 0 && list.every(l => l.isVisible)
    },

    /**
     * Opciones del WidgetSelectDropdown del pill: "Todas las capas" + las capas
     * VISIBLES (sin la "0"), en el formato genérico { value, label, color|icon }.
     */
    activeLayerOptions() {
      const opts = [{ value: null, label: this.$t('all_layers'), icon: 'layer-group' }]
      for (const l of (this.filterLayers || [])) {
        if (l.isVisible && l.name !== '0') {
          opts.push({ value: l.name, label: l.displayName || l.name, color: this._LayerCssColor(l.color) })
        }
      }
      return opts
    },

    /** Filas del WidgetHoverTooltip: longitud total + desglose por estado (con color). */
    hoverTooltipRows() {
      const t = this.advanceTooltip
      const rows = [{ label: `${this.$t('total_length')}: ${t.totalLength.toFixed(2)} m` }]
      for (const brk of t.breakdown) {
        rows.push({ label: `${brk.label || '—'}: ${brk.meters.toFixed(2)} m`, color: brk.color })
      }
      return rows
    },

    /**
     * Herramientas/menús que lista el diálogo de ayuda, según el modo. Labels ya
     * traducidos (reutilizan las claves de los tooltips del toolbar). Cubre
     * diseño (admin) y avance (cliente) para que la ayuda sea útil en ambos.
     */
    helpTools() {
      const t = (k) => this.$t(k)
      const tools = [
        { icon: 'layer-group', label: this.isProgressMode ? t('change_layer') : t('layers') },
        { icon: 'hand-paper',  label: t('move') },
        { icon: 'expand',      label: t('fullscreen') },
        { icon: 'magnifying-glass-plus', label: t('fit_content') },
      ]
      // El menú ☰ de vistas aparece una vez procesado el diseño.
      if (this.hasProcessed) tools.push({ icon: 'bars', label: t('views') })

      if (this.isProgressMode) {
        // Herramientas de avance (no-mechanical).
        tools.push(
          { icon: 'fill-drip',           label: t('highlight_with_progress') },
          { icon: 'eye',                 label: t('status_view_last') },
          { icon: 'location-crosshairs', label: t('register_advance') },
        )
      } else {
        // Herramientas de diseño (admin).
        if (this.profile.design.loadFlow === 'subdivisions') {
          tools.push({ icon: 'gear', label: t('configure_subdivisions') })
        } else {
          tools.push({ icon: 'list-check', label: t('process') })
        }
        tools.push(
          { icon: 'save',   label: t('save') },
          { icon: 'eraser', label: t('clear_viewer') },
        )
      }
      // Fijar el panel lateral (ambos modos).
      tools.push({ icon: 'thumbtack', label: t('pin') })
      return tools
    },
  },

  watch: {
    showLayerPanel(open) {
      if (open) this._RefreshFilterLayers()
    },
    // Si el usuario cambia de vista mientras el panel de capas está abierto,
    // refrescamos la lista para que solo aparezcan las capas relevantes a la vista nueva.
    displayView() {
      if (this.showLayerPanel) this._RefreshFilterLayers()
    },
    /**
     * Si el padre (typicamente el dispatcher de webviewer) pasa un workLayer
     * por la URL — ej. `/pictogram/X/trench_3` — fijamos esa capa como
     * activa INICIAL. Solo polilíneas de esa capa van a permitir registrar
     * avance, y las demás capas (junto con sus carriles de avance pintados)
     * quedan atenuadas. En progress mode el panel de Capas NO se bloquea:
     * el usuario puede cambiar de capa activa sin salir del visor (ver
     * _OnActivateLayer), y el foco visual + el filtro de clicks se recalculan
     * sobre la nueva capa. Si workLayer cambia o vuelve a null, actualizamos.
     */
    workLayer: {
      immediate: true,
      handler(v) {
        if (v) {
          this.activeLayerCode = v
          // El dim se aplica de verdad cuando ya hay viewer + scene cargada;
          // si todavía no, _ApplyLayerDim es no-op y onSceneLoaded lo
          // disparará tras cargar.
          this._ApplyLayerDim()
        }
      },
    },
  },

  mounted() {

  },

  methods: {
    /**
     * Handler único de @action emitido por el Toolbar.
     * Despacha por `handler` y delega a los métodos existentes.
     * Las llamadas a $refs.cadViewer están guardadas porque el viewer
     * puede estar comentado durante la validación.
     */
    _OnToolbarAction({ handler, payload }) {
      switch (handler) {
        case 'toggle-layers-panel':
          this.showLayerPanel = !this.showLayerPanel
          break
        case 'pan':
          this.isDraggable = payload
          if (this.$refs.cadViewer) this.$refs.cadViewer.EnableDraggable(this.isDraggable)
          // Al entrar en modo mano, cerramos cualquier UI interactiva abierta y
          // salimos del modo "Avance en línea" (modos de interacción excluyentes).
          if (this.isDraggable) {
            this._HideTooltip()
            this._CloseInfoPopover()
            if (this.inlineAdvanceActive) this._ToggleInlineAdvanceMode(false)
          }
          break
        case 'fit':        this._OnFitContent(); break
        case 'zoom-in':    this._OnClickZoom(true); break
        case 'zoom-out':   this._OnClickZoom(false); break
        case 'fullscreen': this._OnClickFullScreen(); break
        case 'configure':  this._HandleConfigureSubdivisions(); break
        case 'reset':      this._HandleResetViewer(); break
        case 'save':       this._HandleClickSave(); break
        case 'process':    this._HandleClickProcess(); break
        case 'highlight':  this._OnHighlightSelected(payload); break
        case 'status-view': this._OnStatusViewSelected(payload); break
        case 'inline-advance': this._ToggleInlineAdvanceMode(payload); break
      }
    },

    /**
     * Toggle del modo "Avance en línea" (toolbar). Al activar, cerramos UI que
     * estorbe (info/tooltip) y salimos del modo mano; al desactivar, desarmamos
     * cualquier segmento en curso. Con el modo OFF, el doble-click abre el modal.
     */
    _ToggleInlineAdvanceMode(active) {
      this.inlineAdvanceActive = !!active
      if (active) {
        this._CloseInfoPopover()
        this._HideTooltip()
        if (this.isDraggable) {
          this.isDraggable = false
          this.$refs.cadViewer?.EnableDraggable?.(false)
        }
      } else {
        this._DisarmInlineAdvance()
      }
    },

    /**
     * Botón "Procesar" en no-mechanical: abre el modal de selección de capas.
     * Construye la lista lazy (en el momento de abrir, no en onSceneLoaded) para
     * evitar cómputo innecesario si el usuario nunca llega a procesar.
     * Si ya editó nombres en una apertura anterior, los respetamos pasando los
     * `displayName` actuales mediante `customLayerNames`.
     */
    _HandleClickProcess() {
      if (!this.cadJson) return
      this._BuildLayersList()
      const initial = this.dxfLayersForModal.map(l => ({
        ...l,
        // Si el usuario ya renombró antes, preservamos su edición.
        displayName: this.customLayerNames[l.name] || l.displayName,
      }))
      this.$refs.refSelectLayersModal.show(initial)
    },

    /**
     * Apply del SelectLayersModal: persistimos las capas elegidas (con el nombre
     * que el usuario haya editado) y abrimos SavePictogramModal a continuación.
     * Este es el equivalente al "Configurar → Guardar" del flujo mecánico.
     *
     * @param {Array<{name:string, displayName:string}>} selectedLayers
     */
    _OnSelectLayersApply(selectedLayers) {
      this.selectedLayerCodes = selectedLayers.map(l => l.name)
      // Guardamos los nombres custom indexados por `name` original — los reusamos
      // al construir descriptores y al re-abrir el modal.
      const customNames = {}
      for (const l of selectedLayers) {
        if (l.displayName && l.displayName !== l.name) customNames[l.name] = l.displayName
      }
      this.customLayerNames = customNames
      this.$refs.refSavePictogramModal.show()
    },

    /**
     * Handler único de @action emitido por el Subheader.
     * Reutiliza _HandleOpenPanel que ya existía para gestionar la apertura
     * de los paneles laterales.
     */
    _OnSubheaderAction({ handler, payload }) {
      switch (handler) {
        case 'open-panel-views':  this._HandleOpenPanel('panel-views');  break
        case 'open-panel-layers': this._HandleOpenPanel('panel-layers'); break
        case 'upload-file':
          // Click programático al input invisible. Mismo handler que el dropzone del body.
          this.$refs.fileInputHidden.click()
          break
        case 'back':
          this._HandleReturnClick()
          break
      }
    },

    /**
     * Captura el archivo seleccionado desde el <input type="file"> invisible
     * que se dispara cuando el usuario hace click en "Subir archivo" del header.
     */
    _OnHiddenFileInputChange(event) {
      const file = event.target.files && event.target.files[0]
      if (file) this._OnFileSelected(file)
      // Reseteamos para permitir reseleccionar el mismo archivo después.
      event.target.value = ''
    },

    /**
     * Punto único donde el archivo elegido entra al estado del componente.
     * Lo invocan tanto el dropzone (vía v-model="uploadedFile") como el
     * input invisible del header.
     * TODO: aquí irá la integración con backend cuando esté el endpoint.
     */
    async _OnFileSelected(file) {
      // Si se está cargando un archivo nuevo, descartamos cualquier overlay de
      // avance pintado sobre el archivo anterior (handles distintos, escena nueva).
      this._DisposeAllAdvanceMeshes()
      this._UnbindHoverTooltip()
      this._inlineAdvance.unbind()
      this._DisarmInlineAdvance()
      this._advanceState = Object.create(null) // se re-hidratará tras el load
      // Selección limpia — la lista la reconstruye _BuildLayersList al abrir el modal.
      this.dxfLayersInfo      = []
      this.selectedLayerCodes = []
      this.customLayerNames   = {}
      this.viewerReady = false

      this.uploadedFile = file
      this.cadJson = null
      this.parseError = null
      this.isParsing = true
      this.ShowLoader({ loading: true, text: this.$t('loading_reading_file') })

      try {
        // El parse corre en el worker → el loader repinta la fase en vivo.
        this.cadJson = await parseCadFile(file, {
          onProgress: (p) => {
            const phase = (p && p.phase) || 'parsing'
            let text
            if (phase === 'reading') {
              text = this.$t('loading_reading_file')
            } else {
              text = this.$t('loading_processing_geometry')
              if (p && typeof p.percent === 'number') text += ` ${p.percent}%`
            }
            this.ShowLoader({ loading: true, text })
          },
        })
        // El build de la escena (DxfScene.Build) ocurre en main al setear
        // cadJson; mostramos "Construyendo escena…" hasta que onSceneLoaded
        // apague el loader.
        this.ShowLoader({ loading: true, text: this.$t('loading_building_scene') })
        // El loader se apaga en onSceneLoaded (cuando la escena YA está
        // construida y encuadrada), no aquí — si no, el loader desaparecía y
        // quedaba un hueco en blanco durante el DxfScene.Build (3-4s).
        this._awaitingUploadScene = true
      } catch (err) {
        this.parseError = err.message
        this.uploadedFile = null   // vuelve al dropzone
        this._awaitingUploadScene = false
        this.ShowLoader({ loading: false, text: null })
        this.$toast.open({
          message: err.message,
          type: 'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.isParsing = false
      }
    },

    /**
     * Entrada de scene binario (.epcb) para viewerMode='progress' — el caller
     * (webviewer.vue) descarga el binario de S3 y nos lo pasa como ArrayBuffer.
     *
     * Reconstruimos un `cadJson` sintético con LWPOLYLINE entities por cada
     * polyline decodificada. Setearlo dispara el watcher del Viewer →
     * DxfScene.Build → DxfViewer.LoadScene, igual que cuando llega un DXF
     * parseado. Eso mantiene una sola pipeline de render y reusa overlay
     * de avance, panel de capas, etc.
     *
     * Trade-off vs zero-copy puro: el DxfScene re-procesa el cadJson en main
     * thread. Para esta v1 es aceptable — la ganancia real está en evitar
     * descargar y parsear el DXF original (200MB+). Si después es lento con
     * archivos enormes, se optimiza con LoadScene directo (formato del DxfViewer).
     *
     * @param {ArrayBuffer} buf - .epcb decodificable (validado por decodeScene)
     */
    _LoadBinaryScene(buf) {
      try {
        const decoded = decodeScene(buf)
        logger.info(
          'binary scene decoded:',
          decoded.totalPolys, 'polylines /',
          decoded.totalVerts, 'verts /',
          decoded.layers.length, 'layers'
        )

        // Reset de cualquier estado de un load anterior (mismo patrón que
        // _OnFileSelected). En la práctica progress mode abre una sola vez,
        // pero defensivo no daña.
        this._DisposeAllAdvanceMeshes()
        this._DisposeAllHighlightMeshes()
        this.highlightMode = null
        this._UnbindHoverTooltip()
        this._inlineAdvance.unbind()
        this._DisarmInlineAdvance()
        this._advanceState = Object.create(null)
        this.viewerReady   = false

        // Reconstrucción del cadJson sintético — formato esperado por DxfScene.
        const entities = []
        let entityId = 1
        for (const layer of decoded.layers) {
          for (const poly of layer.polylines) {
            const verts = new Array(poly.vertCount)
            // poly.vertices es Float32Array interleaveado [x0,y0,x1,y1,...]
            for (let i = 0; i < poly.vertCount; i++) {
              verts[i] = { x: poly.vertices[i * 2], y: poly.vertices[i * 2 + 1] }
            }
            entities.push({
              type:     'LWPOLYLINE',
              layer:    layer.code,
              handle:   String(entityId++),
              vertices: verts,
              width:    poly.width || 0,
              shape:    poly.closed,
            })
          }
        }

        // tables.layer.layers — mapping por nombre con color por capa.
        // color 7 = blanco/negro (default DXF) cuando no hay color explícito.
        const layerEntries = {}
        for (const layer of decoded.layers) {
          layerEntries[layer.code] = {
            name:  layer.code,
            color: layer.color || 7,
          }
        }

        // Setear `cadJson` dispara el watcher del Viewer y carga la escena.
        // `hasFile` pasa a true → el placeholder de "Cargando diseño…" se va.
        this.cadJson = {
          header:   {},
          blocks:   {},
          tables:   { layer: { layers: layerEntries } },
          entities,
        }
      } catch (err) {
        logger.error('_LoadBinaryScene error:', err)
        this.$buefy?.toast?.open?.({
          message:  this.$t('error_loading_scene') || err.message || 'Error cargando escena',
          type:     'is-danger',
          position: 'is-top-right',
        })
      }
    },

    ShowLoader(data) {
      this.isLoading = data.loading;
      this.customLoaderText = data.text
    },

    _OnToggleLayer(layer, newState) {
        layer.isVisible = newState
        // Persistimos el toggle en la fuente de verdad para que el estado
        // sobreviva al reabrir el panel (antes se perdía: isVisible se
        // reconstruía siempre a true).
        this.layerVisibility[layer.name] = newState
        this.$refs.cadViewer.ShowLayer(layer.name, newState)
        // Los carriles de avance viven fuera de las capas del motor → hay que
        // ligar su visibilidad a mano (si no, al ocultar la capa quedan flotando).
        this._advanceOverlay.setLayerVisible(layer.name, newState)
    },

    _OnToggleAll(newState) {
        if (this.filterLayers) {
            for (const layer of this.filterLayers) {
                if (layer.isVisible !== newState) {
                    this._OnToggleLayer(layer, newState)
                }
            }
        }
    },

    /** Cierra el panel lateral y lo desacopla (reset del pin). */
    _CloseLayersSidebar() {
        this.showLayerPanel = false
        this.sidebarPinned  = false
    },

    /** Acciones de cabecera del sidebar (WidgetSidebar emite epc-action(key)). */
    _OnSidebarAction(key) {
        if (key === 'toggle-all-layers') this._OnToggleAll(!this.allLayersVisible)
    },

    /** Abre el diálogo de ayuda (herramientas/menús del visor) en el modal. */
    _OnClickHelp() {
        this.$refs.helpModal.show({
            component: HelpDialog,
            props: {
                intro:      this.$t('help_intro'),
                tools:      this.helpTools,
                toolsTitle: this.$t('tools_and_view'),
            },
            title: this.$t('help'),
            width: 560,
            scrollBody: true,
        })
    },

    /**
     * Click en el nombre de una capa del LayersPanel — activa/desactiva
     * el filtro de clicks. Cuando hay una activa, solo polilíneas de esa
     * capa permiten registrar avance.
     */
    _OnActivateLayer(code) {
      // La activación de capa solo aplica al cliente registrando avance.
      // En admin (viewerMode='design') el panel "Capas" sirve solo para
      // toggle de visibilidad — no hay concepto de capa "activa".
      if (!this.isProgressMode) return
      this.activeLayerCode = code
      // Al cambiar de capa, los estados disponibles cambian; volvemos al modo
      // "último" para no dejar seleccionado un estado que la nueva capa no
      // tiene (lo que dejaría todo sin pintar). Repintamos con el filtro nuevo.
      if (this.statusViewMode !== 'last' && this.statusViewMode !== 'all') {
        this.statusViewMode = 'last'
      }
      this._ApplyLayerDim()
      this._RepaintAllAdvance()
      // Tras elegir una capa, cerramos el panel: el usuario ya seleccionó y
      // vuelve directo al viewer para registrar avance sobre esa capa.
      this.showLayerPanel = false
    },

    /** Selección desde el dropdown del pill (value = code de capa o null = todas). */
    _SelectActiveLayer(code) {
      this._OnActivateLayer(code ?? null)
    },

    /** Al abrir el dropdown refrescamos la lista de capas para que esté al día. */
    _OnLayerSwitcherChange(open) {
      if (open) this._RefreshFilterLayers()
    },

    /** Convierte el color de capa (int del DXF) a css hex para el swatch. */
    _LayerCssColor(value) {
      let s = Number(value || 0).toString(16)
      while (s.length < 6) s = '0' + s
      return '#' + s
    },

    /** Abre el modal con el detalle de una capa (icono info de la lista). */
    _OnLayerInfo(layer) {
      const info = this._ComputeLayerInfo(layer.name, layer.displayName || layer.name, this._LayerCssColor(layer.color))
      this.$refs.layerInfoModal.show({
        component: LayerInfoModal,
        props: { info },
        title: this.$t('layer_info'),
        width: 520,
        scrollBody: true,
      })
    },

    /**
     * Agrega el detalle de una capa: nº de elementos trackeables, longitud total
     * y longitud por estado (reutiliza computeHoverBreakdown por entidad, misma
     * fuente que el tooltip). Primer paso; se irá ampliando.
     */
    _ComputeLayerInfo(layerCode, layerName, layerColor) {
      const pictogramLayer = (this.pictogram?.layers || []).find(l => l.code === layerCode)
      const statuses = pictogramLayer ? this._GetStatusesForLayer(pictogramLayer) : []
      // La MÉTRICA la decide el PERFIL (tipo), no el modo: electrical/trench/evac
      // miden por longitud; mecánico por conteo de elementos. Igual en diseño y avance.
      const metric = this.profile?.layerInfo?.metric || 'length'

      // ── LONGITUD (electrical / trenches / evacuación): longitud total + por estado (m) ──
      if (metric === 'length') {
        const labels = { pending: this.$t('pending_execution'), rejected: this.$t('rejected') }
        const entities = this._GetTrackableEntities().filter(e => e.layer === layerCode)
        const count = entities.length
        let totalLength = 0
        const byValue = new Map()   // value → { label, color, meters }
        for (const entity of entities) {
          const len = getEntityTotalLength(entity) || 0
          totalLength += len
          const data = this._advanceState[String(entity.handle)]
          const breakdown = computeHoverBreakdown(data, statuses, len, labels)
          for (const b of breakdown) {
            const prev = byValue.get(b.value) || { label: b.label, color: b.color, meters: 0 }
            prev.meters += b.meters || 0
            byValue.set(b.value, prev)
          }
        }
        const rows = [...byValue.entries()]
          .filter(([, r]) => r.meters > 1e-6)
          .map(([key, r]) => ({
            key, label: r.label, color: r.color,
            text: `${r.meters.toFixed(2)} m`,
            pct: totalLength > 0 ? Math.round((r.meters / totalLength) * 100) : 0,
          }))
        return {
          layerName, layerColor,
          subtitle:      `${count} ${this.$t('elements')}`,
          headlineLabel: this.$t('total_length'),
          headlineValue: `${totalLength.toFixed(2)} m`,
          sectionTitle:  this.$t('length_by_state'),
          rows,
        }
      }

      // ── CONTEO (mecánico): total de ELEMENTOS + conteo por estado del pictograma ──
      const entities = this._GetLayerEntities(layerCode)
      const count = entities.length
      const byValue = new Map()   // value → { label, color, count }
      for (const entity of entities) {
        const st = this._CurrentStateOfEntity(entity, statuses)
        const prev = byValue.get(st.value) || { label: st.label, color: st.color, count: 0 }
        prev.count++
        byValue.set(st.value, prev)
      }
      const rows = [...byValue.entries()]
        .filter(([, r]) => r.count > 0)
        .map(([key, r]) => ({
          key, label: r.label, color: r.color,
          text: String(r.count),
          pct: count > 0 ? Math.round((r.count / count) * 100) : 0,
        }))
      return {
        layerName, layerColor,
        subtitle:      '',
        headlineLabel: this.$t('elements'),
        headlineValue: String(count),
        sectionTitle:  this.$t('total_by_state'),
        rows,
      }
    },

    /** Entidades del cadJson en una capa (para el conteo mecánico, no solo polilíneas). */
    _GetLayerEntities(layerCode) {
      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return []
      const raw = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      return list.filter(e => e && e.layer === layerCode)
    },

    /**
     * Estado vigente de UN elemento para el conteo "por estado" (diseño): el
     * status del último segmento registrado, o "sin iniciar" si no tiene avance.
     * Simple (no aplica flujo de rechazos); suficiente para el conteo.
     */
    _CurrentStateOfEntity(entity, statuses) {
      const data = this._advanceState[String(entity.handle)]
      const segs = (data && data.segments) || []
      if (segs.length) {
        const last = segs[segs.length - 1]
        const st = (statuses || []).find(s => s.value === last.status)
        if (st) return { value: st.value, label: st.label, color: st.color }
        return { value: last.status, label: last.statusLabel || '—', color: last.statusColor || '#9ca3af' }
      }
      return { value: '__pending', label: this.$t('pending_execution'), color: '#9ca3af' }
    },

    /**
     * Atenúa las capas no-activas en el render para reforzar visualmente
     * que solo la activa es interactiva. Sin capa activa, restaura todas
     * a su color original. Solo aplica en viewerMode='progress'.
     *
     * Las capas no-activas se pintan de un GRIS plano y neutro (no se aclaran
     * hacia blanco): así pierden su color y la capa activa —a color pleno—
     * resalta. También se agrisan los carriles de avance ya pintados de esas
     * capas, para que ni el dibujo ni los registros previos distraigan.
     */
    _ApplyLayerDim() {
      if (!this.isProgressMode) return
      const viewer = this.$refs.cadViewer
      if (!viewer || typeof viewer.SetLayerGrayscale !== 'function') return
      const layers = viewer.GetLayers(true) || []
      const active = this.activeLayerCode
      // Con el resaltado activo TODA la escena va a gris (la única fuente de
      // color pasa a ser el overlay de resaltado); sin resaltado, solo se
      // agrisan las capas no-activas y la activa mantiene su color.
      const highlightOn = !!this.highlightMode
      for (const l of layers) {
        const gray = highlightOn ? true : !!(active && l.name !== active)
        viewer.SetLayerGrayscale(l.name, gray, DIM_LAYER_GRAY)
      }
      // Los carriles de avance viven en THREE.Groups aparte (no son "capas"
      // del DxfViewer), así que SetLayerGrayscale no los toca — los agrisamos
      // manualmente según la capa a la que pertenece cada grupo.
      this._ApplyAdvanceMeshesDim()
    },

    /**
     * Recorre los carriles de avance pintados (_advanceMeshes) y ajusta su
     * opacidad según la capa activa: los de la capa activa (o todos, si no
     * hay ninguna activa) quedan a opacidad plena; los de otras capas se
     * atenúan. Fuerza un render al final para reflejar el cambio.
     */
    _ApplyAdvanceMeshesDim() {
      // Delega el dim de los carriles al overlay (posee los meshes).
      this._advanceOverlay.dim(this.activeLayerCode, !!this.highlightMode)
    },

    /**
     * Reconstruye `filterLayers` (lo que muestra el panel de Capas) según la vista activa.
     * Solo incluye layers que pertenecen a `displayView` (o son layers de texto, siempre visibles).
     * Se invoca al abrir el panel y cuando cambia displayView estando el panel abierto.
     */
    _RefreshFilterLayers() {
      if (!this.$refs.cadViewer) return

      // En progress mode (cliente), las capas trackeables del .epcb tienen
      // como `name` el code numerado (electrical_n / trench_n). Para mostrar
      // un nombre humano en el panel, resolvemos `pictogram_layer.name`
      // por code. Internamente se mantiene `name = code` para que las
      // llamadas a viewer.ShowLayer(name) sigan funcionando.
      const nameByCode = Object.create(null)
      if (this.isProgressMode) {
        for (const pl of (this.pictogram?.layers || [])) {
          if (pl?.code && pl?.name) nameByCode[pl.code] = pl.name
        }
      }

      // La transformación (filtrar por vista + mapear visibilidad/nombres) vive en
      // filter-layers.helper (pura); aquí solo reunimos los inputs reactivos.
      this.filterLayers = buildFilterLayers(this.$refs.cadViewer.GetLayers(true), {
        displayView:     this.displayView,
        layerVisibility: this.layerVisibility,
        nameByCode,
        textLayers:      this.textLayers,
      })
    },

    /**
     * Cambia la vista activa (Vistas panel).
     * Aplica la visibilidad correspondiente al DxfViewer iterando todas sus layers.
     */
    _OnViewSelected(code) {
      this.displayView = code
      this._ApplyDisplayView(code)
    },

    /** El ☰ emite epc-open-panel('view-<code>'); extraemos el code y cambiamos vista. */
    _OnMenuViewSelected(payload) {
      const code = String(payload).replace(/^view-/, '')
      this._OnViewSelected(code)
    },

    /**
     * Aplica una vista: la visibilidad base (computeViewVisibility) reemplaza los
     * toggles manuales previos — siembra la fuente de verdad reactiva y la aplica
     * al motor. El mapeo layer→vista vive en view-filter.helper.
     */
    _ApplyDisplayView(code) {
      if (!this.$refs.cadViewer) return
      const visibility = computeViewVisibility(this.$refs.cadViewer.GetLayers(), code, this.textLayers)
      this.layerVisibility = visibility
      for (const name in visibility) {
        this.$refs.cadViewer.ShowLayer(name, visibility[name])
        // Mantener los carriles de avance en sync con la visibilidad de la capa.
        this._advanceOverlay.setLayerVisible(name, visibility[name])
      }
    },

    _OnClickZoom(zoomIn) {
      if (!this.$refs.cadViewer) return
      if( zoomIn )
        this.$refs.cadViewer.ZoomIn()
      else
        this.$refs.cadViewer.ZoomOut()
      // El zoom por botón cambia camera.zoom directamente (no vía OrbitControls),
      // así que no emite 'viewChanged' → sincronizamos el % aquí a mano.
      this._SyncZoomPercent()
    },

    /**
     * Sincroniza el % mostrado en el control de zoom flotante con el zoom real
     * de la cámara ortográfica. En fit, camera.zoom = 1 → 100%. Se dispara por
     * botón (_OnClickZoom) y por 'viewChanged' (scroll/pan/fit del OrbitControls).
     */
    _SyncZoomPercent() {
      const cam = this.$refs.cadViewer?.GetDxfViewer?.()?.GetCamera?.()
      if (cam) this.zoomPercent = Math.round(cam.zoom * 100)
    },

    _OnClickFullScreen() {
      const element = this.$refs.viewer_page
      if (isFullScreenActive()) exitFullScreen()
      else                      requestFullScreen(element)
    },

    _OnFitContent() {
      if (!this.$refs.cadViewer) return
      const dxf = this.$refs.cadViewer.GetDxfViewer && this.$refs.cadViewer.GetDxfViewer()
      // Fit ROBUSTO: si hay outliers (ej. XREF mal colocados que inflan el bbox
      // y dejan el dibujo como una mota invisible), encuadramos al cluster
      // principal ignorándolos. El render NO cambia (this.bounds intacto); solo
      // la cámara. Si no hay outliers, fit normal a toda la escena.
      const robust = (dxf && dxf.HasRenderer && dxf.HasRenderer())
        ? computeRobustBounds(this.processedCadJson || this.cadJson)
        : null
      if (robust && robust.removed > 0) {
        const o = dxf.GetOrigin()
        dxf.FitView(robust.minX - o.x, robust.maxX - o.x, robust.minY - o.y, robust.maxY - o.y)
      } else {
        this.$refs.cadViewer.FitToScreen()
      }
      // SetView (dentro del fit) reposiciona la cámara pero no siempre dispara
      // el 'change' de OrbitControls que pinta el frame → forzamos Render. Y
      // sincronizamos el % (fit → zoom 1 = 100%).
      this.$refs.cadViewer.Render()
      this._SyncZoomPercent()
    },

    /**
     * Abre el modal de configuración de subdivisiones pasándole el cadJson completo.
     * El modal se encarga de extraer las capas y calcular el total de estructuras.
     */
    _HandleConfigureSubdivisions() {
      if (!this.cadJson) return
      // Pasamos la config ya aplicada para rehidratar el modal (no perder la
      // selección "por capas" ni las configuraciones al reabrir).
      this.$refs.refConfigureSubdivisionsModal.show(this.cadJson, {
        cadLayeredDesign: this.cadLayeredDesign,
        createElements:   this.createElements,
      })
    },

    /**
     * Pregunta al usuario antes de resetear el visor.
     * Si confirma, dispara _DoReset() que limpia todo el estado del archivo cargado.
     */
    _HandleResetViewer() {
      this.$refs.confirmModal.show({
        component: WidgetDialog,
        title:     this.$t('clean'),
        width:     440,
        props: {
          message: this.$t('confirm_reset_viewer'),
          tone:    'warning',
          icon:    'triangle-exclamation',
          actions: [
            { key: 'cancel',  label: this.$t('cancel'), variant: 'ghost' },
            { key: 'confirm', label: this.$t('accept'), variant: 'primary' },
          ],
        },
        listeners: {
          'epc-action': (key) => { if (key === 'confirm') this._DoResetViewer() },
        },
      })
    },
    /**
     * Resetea el estado del LayoutBuilder al inicial (sin archivo cargado).
     *   - uploadedFile y cadJson → null
     *   - hasFile (computed) pasa a false
     *   - El Toolbar se oculta automáticamente (v-if="hasFile")
     *   - El dropzone vuelve a aparecer (v-if="!hasFile")
     *   - El watcher del Viewer detecta el cambio a null y llama dxfViewer.Clear()
     *   - Reset del input invisible para permitir re-seleccionar el mismo archivo
     */
    _DoResetViewer() {
      this.uploadedFile = null
      this.cadJson      = null
      this.parseError   = null

      this.processedCadJson    = null
      this.totalEntitiesDesign = {}
      this.cadLayeredDesign    = null
      this.createElements      = { pilings: false, modules: false }

      this.showLayerPanel = false
      this.sidebarPinned  = false
      this.filterLayers   = []
      this.layerVisibility = {}

      this.showPanelViews = false
      this.displayView    = DEFAULT_VIEW

      // Selección de capas (no-mechanical) — la próxima carga reconstruirá
      // la lista desde el nuevo DXF.
      this.dxfLayersInfo      = []
      this.selectedLayerCodes = []
      this.customLayerNames   = {}

      // Permite re-seleccionar el mismo archivo después
      if (this.$refs.fileInputHidden) {
        this.$refs.fileInputHidden.value = ''
      }
    },

    async _HandleClickSave() {
      // En mechanical_installation el procesamiento de subdivisiones (pilings,
      // modules, panels) es obligatorio antes de guardar — el pictograma no
      // tiene sentido sin esas entidades. Para el resto de tipos (electrical,
      // trenches, evacuation) el procesamiento no aplica y se puede guardar
      // directamente con el cadJson del parser.
      if (!this.hasProcessed && this.profile.design.requiresProcessing) {
        this.$toast.open({
          message:  this.$t('process_first'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }

      // En modo 'upload-new-version' no abrimos el modal de crear pictograma —
      // el pictograma ya existe. Disparamos el flujo de subir nueva versión.
      if (this.mode === 'upload-new-version') {
        await this._HandleClickContinueAsNewVersion()
        return
      }

      this.$refs.refSavePictogramModal.show()
    },

    /**
     * Stub temporal. En sub-paso C.4 implementamos:
     *   - Upload del DXF como nueva DocumentVersion
     *   - Upload del JSON parseado a temp S3
     *   - Redirect a la misma ruta con ?temp_key=...&doc_version_id=...
     *   - ProcessPictogramPage renderiza UpdatePictogramFlow al detectar los query params
     */
    async _HandleClickContinueAsNewVersion() {
      if (!this.pictogram?.id || !this.pictogram?.document_id) {
        this.$toast.open({
          message:  'Falta información del pictograma (id o document_id).',
          type:     'is-danger',
          position: 'is-top-right',
        })
        return
      }

      try {
        this.ShowLoader({ loading: true, text: this.$t('uploading_cad_file') })

        const { documentVersionId, sceneKey } = await uploadNewPictogramVersion({
          dxfFile:     this.uploadedFile,
          cadJson:     this.processedCadJson,
          documentId:  this.pictogram.document_id,
          pictogramId: this.pictogram.id,
          onProgress:  (key) => this.ShowLoader({ loading: true, text: this.$t(key) }),
        })

        // Redirect a la misma URL con query params — ProcessPictogramPage va a
        // detectar temp_key y renderizar el modo 'review' en sub-paso C.5.
        this.$router.replace({
          name: 'project.admin.pictogram.process-update',
          params: {
            project_id: this.entityId,
            id:         this.pictogram.id,
          },
          query: {
            temp_key:       sceneKey,
            doc_version_id: documentVersionId,
          },
        })
      } catch (err) {
        logger.error('upload new version error:', err)

        // Backend rechazó porque ya hay una versión pendiente para este pictograma.
        // Ofrecer al usuario ir al PictogramConfig donde puede continuar el review
        // o descartar la pendiente antes de reintentar.
        if (err.name === 'PendingVersionExists') {
          this.$buefy.dialog.confirm({
            title:        this.$t('pending_version_exists_title') || 'Versión pendiente',
            message:      err.message,
            confirmText:  this.$t('go_to_pictogram') || 'Ir al pictograma',
            cancelText:   this.$t('cancel') || 'Cancelar',
            type:         'is-warning',
            hasIcon:      true,
            onConfirm: () => {
              this.$router.replace({
                name: 'project.admin.pictogram.config',
                params: {
                  project_id: this.entityId,
                  id:         this.pictogram.id,
                },
              })
            },
          })
        } else {
          this.$toast.open({
            message:  this.$t(err.message) || err.message || this.$t('save_error'),
            type:     'is-danger',
            position: 'is-top-right',
          })
        }
      } finally {
        this.ShowLoader({ loading: false, text: null })
      }
    },

    _HandleOpenPanel(namePanel) {
      if (namePanel === 'panel-views')  this.showPanelViews = !this.showPanelViews
      if (namePanel === 'panel-layers') this.showLayerPanel = !this.showLayerPanel
    },

    /**
     * Navegación de "volver atrás" del subheader.
     * Si el pictograma trae info de actividad/sub-actividad, redirige a esa
     * vista (mismo patrón que viewer-pixi/ViewerPageKonva). Si no, usa el
     * historial del navegador como fallback.
     */
    _HandleReturnClick() {
      const activities = this.pictogram && this.pictogram.activities
      if (activities && activities.length > 0) {
        const first = activities[0]
        if (first.parent_id !== null) {
          this.$router.push({
            name: 'subactivity.home',
            params: { subactivity_id: first.id, activity_id: first.parent_id }
          })
        } else {
          this.$router.push({
            name: 'activity.home',
            params: { activity_id: first.id }
          })
        }
        return
      }
      // Fallback: volver a la página anterior del historial.
      this.$router.back()
    },

    // ───────────────── Handlers del SaveSuccessModal ─────────────────

    /** Cerrar el modal y quedar en la vista actual con el pictograma cargado. */
    _OnSaveSuccessClose() {
      // no-op: el usuario sigue trabajando con el pictograma actual
    },

    /** Limpiar el visor y volver al dropzone para cargar un nuevo DXF. */
    _OnSaveSuccessNew() {
      this._DoResetViewer()
    },

    /** Volver al listado de pictogramas (misma navegación que el botón "Atrás"). */
    _OnSaveSuccessBack() {
      this._HandleReturnClick()
    },

    _AllowedAction(action) {
      if (Object.keys(this.canActionsPictogram).length === 0)
        return false;

      return this.canActionsPictogram[action] === 'undefined' ? false : this.canActionsPictogram[action]
    },



    onSceneLoaded(e) {
      logger.info('scene loaded')
      // Aplicamos la vista activa inmediatamente al cargar la escena (antes del primer paint),
      // para evitar el flash en el que el viewer renderiza con TODAS las layers visibles y
      // medio segundo después se ocultan las que no pertenecen a la vista.
      // Solo aplica cuando ya hay processedCadJson (tras configurar subdivisiones).
      if (this.hasProcessed && this.$refs.cadViewer) {
        this._ApplyDisplayView(this.displayView)
      }
      // Flag para que el overlay interactivo se monte solo después de que la
      // escena Three.js esté lista (sino `$refs.cadViewer.GetDxfViewer()` devuelve
      // null). El template pasa `$refs.cadViewer` directo al overlay — NO se
      // guarda en data() porque Vue observaría el árbol completo del Viewer
      // (renderer, materials, geometries) y eso cuelga el thread.
      this.viewerReady = true

      // Refleja el estado por defecto del modo mano en el cursor (OFF → cursor
      // normal). El pan por arrastre está siempre disponible.
      this.$refs.cadViewer.EnableDraggable(this.isDraggable)

      // Encuadre robusto tras cargar. El viewer auto-encuadra a TODA la escena,
      // pero si hay outliers (XREF mal colocados muy lejos que inflan el bbox)
      // el dibujo queda como una mota invisible. _OnFitContent detecta outliers
      // y encuadra el cluster real; si no hay, es el fit normal (idempotente).
      this.$nextTick(() => {
        this._OnFitContent()
        // Apagamos el loader del upload AQUÍ (escena ya construida y encuadrada),
        // no en el finally de _OnFileSelected → evita el hueco en blanco.
        if (this._awaitingUploadScene) {
          this._awaitingUploadScene = false
          this.ShowLoader({ loading: false, text: null })
        }
      })

      // Avance lineal: solo si el perfil lo tiene Y viewerMode === 'progress'.
      // En 'design' (admin) nunca se inicializa el overlay / hover tooltip —
      // el admin carga y guarda, el avance se registra desde el viewer cliente.
      if (this.profile.progress.linearAdvance && this.isProgressMode) {
        this.$nextTick(() => {
          this._RehydrateAdvance()
          this._BindHoverTooltip()
          if (this.profile.progress.inlineAdvance) this._inlineAdvance.bind()
          // Si ya había activeLayerCode (por workLayer en la URL), recién
          // ahora podemos aplicar el dim sobre las capas ya cargadas.
          this._ApplyLayerDim()
        })
      }
    },

    /**
     * Construye la lista de capas que alimenta el SelectLayersModal combinando:
     *   - GetLayers(true) del DxfViewer → name/displayName/color por capa visible
     *   - computeLayerMetrics(cadJson)   → length y count por capa
     * Excluye las capas que sólo contienen TEXT/MTEXT (vienen de textLayers,
     * ya computado en otro computed por el panel de filtro).
     * Por defecto todas las capas arrancan seleccionadas en la primera apertura;
     * si el usuario vuelve a abrir el modal sin cambiar de DXF se preserva la
     * selección previa.
     */
    _BuildLayersList() {
      if (!this.$refs.cadViewer) {
        this.dxfLayersInfo      = []
        this.selectedLayerCodes = []
        return
      }
      const viewerLayers = this.$refs.cadViewer.GetLayers(true) || []
      const metrics      = computeLayerMetrics(this.cadJson)
      const textSet      = this.textLayers || new Set()

      this.dxfLayersInfo = viewerLayers
        .filter(l => !textSet.has(l.name))
        .map(l => {
          const m = metrics[l.name] || { length: 0, count: 0 }
          return {
            name:         l.name,
            displayName:  l.displayName,
            color:        l.color,
            entityCount:  m.count,
            lengthMeters: m.length,
          }
        })
      this.selectedLayerCodes = this.dxfLayersInfo.map(l => l.name)
    },

    onClick(e) {
      logger.info('click on scene:', e.position)
    },

    // ─── Avance lineal sobre polilíneas con ancho (solo no-mechanical) ───────
    //
    // Flow:
    //   1. DxfInteractiveOverlay emite epc-entity-click con {handle, entity}
    //   2. Validamos que sea polilínea con ancho > 0 y abrimos el modal
    //   3. Usuario elige dirección + metros, aplicamos → pintamos quads
    //      translúcidos encima del ribbon (THREE.Group por handle).
    //
    // Sin persistencia: al recargar/cambiar archivo se descartan los meshes.

    /**
     * Filtro del overlay — CRÍTICO para memoria.
     *
     * El overlay crea un hitbox (PlaneGeometry + Material) por cada segmento
     * de cada entity que pase este filtro. Un DXF puede traer decenas de
     * miles de polilíneas wireframe (símbolos en bloques, decoración) y
     * generar hitboxes para todas agota memoria del navegador.
     *
     * Como esta feature solo opera sobre polilíneas con ancho (las que el
     * render dibuja como ribbon), filtramos por `width > 0`. Eso descarta
     * el 99% de las polilíneas wireframe.
     */
    _AdvanceOverlayFilter(entity) {
      if (!entity) return false
      if (entity.type !== 'POLYLINE' && entity.type !== 'LWPOLYLINE') return false
      return getEntityMaxWidth(entity) > 0
    },

    /**
     * UN CLICK sobre una polilínea trackeable → popover de INFO (campos extra +
     * certificación por metros + progreso). El doble click abre el formulario
     * de avance (_OnEntityClickForAdvance). Mismos filtros de entidad/capa.
     */
    _OnEntityClickForInfo({ handle, entity, clientX, clientY }) {
      // En modo avance inline (segmento armado) no abrimos la info al click: la
      // interacción es marcar el avance; la info vuelve al desarmar.
      if (this.inlineArmedHandle != null) return
      if (!entity || !this.profile.progress.linearAdvance) return
      if (getEntityMaxWidth(entity) <= 0) return
      // No interactuar con capas ocultas (el hitbox sigue existiendo aunque la
      // geometría/carril estén ocultos).
      if (this.layerVisibility[entity.layer] === false) return

      const pictogramLayer = (this.pictogram?.layers || []).find(l => l.code === entity.layer)
      if (!pictogramLayer) return
      if (this.activeLayerCode && entity.layer !== this.activeLayerCode) return

      // Ocultar el tooltip de hover para que no se pise con el popover.
      this.advanceTooltip.visible = false

      // Abrir el popover en la posición del click, en estado "cargando".
      this.infoPopover.position = { x: clientX || 0, y: clientY || 0 }
      this.infoPopover.data     = null
      this.infoPopover.loading  = true
      this.infoPopover.visible  = true

      // Contorno del segmento (line punteado + resaltado) siguiendo la cámara.
      this._TrackInfoSegment(entity)

      getElectricalComponentInfo(this.pictogram.id, String(handle), pictogramLayer.code)
        .then(({ data }) => {
          if (!this.infoPopover.visible) return
          this.infoPopover.data = (data && data.success) ? data.component : null
        })
        .catch((err) => {
          logger.error('getElectricalComponentInfo failed:', err)
          this.infoPopover.data = null
        })
        .finally(() => {
          this.infoPopover.loading = false
        })
    },

    /**
     * Proyecta el contorno del segmento a pantalla y lo mantiene sincronizado
     * (rAF) mientras el popover está abierto — así el resaltado + el leader line
     * siguen al segmento aunque el usuario haga pan/zoom.
     */
    _TrackInfoSegment(entity) {
      cancelAnimationFrame(this._infoRaf)
      const step = () => {
        if (this._isBeingDestroyed || this._isDestroyed || !this.infoPopover.visible) return
        const pts = this.$refs.refDxfOverlay?.ProjectEntityToScreen?.(entity) || []
        this.infoPopover.segmentPoints = pts
        if (pts.length) {
          let sx = 0, sy = 0
          for (const p of pts) { sx += p.x; sy += p.y }
          this.infoPopover.anchor = { x: sx / pts.length, y: sy / pts.length }
        } else {
          this.infoPopover.anchor = null
        }
        this._infoRaf = requestAnimationFrame(step)
      }
      this._infoRaf = requestAnimationFrame(step)
    },

    /** Cierra el popover de info y corta el loop de seguimiento del segmento. */
    _CloseInfoPopover() {
      this.infoPopover.visible = false
      cancelAnimationFrame(this._infoRaf)
      this.infoPopover.segmentPoints = []
      this.infoPopover.anchor = null
    },

    _OnEntityClickForAdvance({ handle, entity }) {
      // Doble-click = ACTIVAR el registro de avance. Dos vías excluyentes según el
      // perfil (una u otra, nunca ambas):
      //   - inline (modo "Avance en línea" ACTIVO en el toolbar): arma el gesto
      //     sobre ESTE segmento (click inicio A/B + arrastre + popover). El resto
      //     (click simple = info, hover, pan) sigue intacto.
      //   - modal (modo OFF / flujo histórico): abre RegisterAdvanceModal.
      if (this.inlineAdvanceEnabled && this.inlineAdvanceActive) {
        this._ArmInlineAdvance(handle, entity)
        return
      }
      if (!entity || !this.profile.progress.linearAdvance) return
      // No registrar avance sobre capas ocultas.
      if (this.layerVisibility[entity.layer] === false) return
      // Filtramos por ancho: solo polilíneas que el render dibuja como ribbon.
      const width = getEntityMaxWidth(entity)
      if (width <= 0) return

      const geomLength = getEntityTotalLength(entity)
      if (geomLength <= 0) return

      // Lookup del pictogram_layer al que pertenece esta polilínea.
      // `entity.layer` es el code que el encoder puso en el .epcb:
      //   - Capas trackeables (seleccionadas en admin): coincide con
      //     pictogram_layer.code (electrical_n / trench_n).
      //   - Capas de contexto visual: es el nombre DXF original, no
      //     matchea ningún pictogram_layer → no se permite registrar avance.
      //
      // Si el pictograma fue guardado antes de este cambio, sus polilíneas
      // tienen el nombre DXF como code y no van a matchear → re-guardar
      // desde admin para regenerar el .epcb con los codes correctos.
      const pictogramLayer = (this.pictogram?.layers || [])
        .find(l => l.code === entity.layer)
      if (!pictogramLayer) return

      // Filtro adicional: si el usuario activó una capa específica en el
      // LayersPanel, solo respondemos a clicks sobre esa capa. Esto evita
      // registrar avance por error sobre polilíneas de otras capas que
      // estén cerca o superpuestas.
      if (this.activeLayerCode && entity.layer !== this.activeLayerCode) return

      // Si ya hay avance guardado para ese handle precargamos los valores —
      // el modal funciona también como edición, no solo como alta.
      // Backward-compat: si el state viejo (sin segments) existe, lo
      // promovemos a un único segment para no perder data.
      const existing = upgradeAdvanceState(this._advanceState[String(handle)])
      if (existing) this._advanceState[String(handle)] = existing

      // totalLength a 2 decimales (misma precisión que los metros que se
      // registran). CRÍTICO para la elegibilidad de estados: si usáramos la
      // longitud cruda de la geometría (ej. 27.8034) los metros (2 dec., máx
      // 27.80) nunca la alcanzarían y `cap` nunca llegaría a 0 → el estado
      // completo no se bloquearía ni habilitaría el siguiente. Al reabrir un
      // segmento con avance usamos su total_length guardado (ya redondeado).
      const totalLength = (existing && existing.totalLength != null)
        ? Number(existing.totalLength)
        : Math.round(geomLength * 100) / 100

      // Resolver los statuses que verá el usuario en el select:
      //   - Si pictogram_layer tiene statuses configurados → usarlos
      //     (cada uno con su id, name y color reales de la BD).
      //   - Si no → fallback al array hardcoded ADVANCE_STATUSES.
      const statuses = this._GetStatusesForLayer(pictogramLayer)

      // entity/handle/totalLength/pictogramLayer/statuses en propiedad de
      // instancia (no reactiva): el entity tiene arrays de vertices que si
      // Vue observara recursivamente colgaría el thread. Los statuses los
      // guardamos para resolver color al pintar y persistir avance.
      this._advanceCurrent = { handle, entity, totalLength, pictogramLayer, statuses }

      this.$refs.refRegisterAdvanceModal.show({
        totalLength,
        vertices:  entity.vertices,
        direction: existing?.direction || 'l2r',
        directionLocked: !!existing,
        // Modelo de segmentos: cada apply agrega un tramo nuevo, no
        // modifica los existentes. El modal los renderiza como barra
        // multi-color + lista por status.
        previousSegments: existing?.segments || [],
        statuses,
        // Modo de orden de estados (free | strict | capped) resuelto con
        // herencia capa → pictograma → default. El modal usa esto para
        // deshabilitar estados no elegibles y capar el máximo del input.
        orderMode: this._ResolveStatusOrderMode(pictogramLayer),
      })
    },

    /**
     * Contexto de negocio de un handle para el GESTO de avance inline. Replica las
     * mismas guardas que _OnEntityClickForAdvance (capa visible/trackeable, ancho de
     * ribbon, longitud, capa activa) y devuelve lo que el controller necesita, o null
     * para ignorar el gesto. NO abre modal: el popover de estado/fecha es F3.
     */
    _GetAdvanceContext(handle, entity) {
      if (!entity || !this.profile.progress.linearAdvance) return null
      if (this.layerVisibility[entity.layer] === false) return null
      if (getEntityMaxWidth(entity) <= 0) return null
      const geomLength = getEntityTotalLength(entity)
      if (geomLength <= 0) return null
      const pictogramLayer = (this.pictogram?.layers || []).find(l => l.code === entity.layer)
      if (!pictogramLayer) return null
      if (this.activeLayerCode && entity.layer !== this.activeLayerCode) return null

      const existing = upgradeAdvanceState(this._advanceState[String(handle)])
      if (existing) this._advanceState[String(handle)] = existing
      // Mismo redondeo a 2 decimales que el modal (crítico para la elegibilidad).
      const totalLength = (existing && existing.totalLength != null)
        ? Number(existing.totalLength)
        : Math.round(geomLength * 100) / 100

      // Auto-objetivo multi-estado (Opción A): el delta continúa desde el frente del
      // ESTADO objetivo (no del nivel 0 global). Al concluir un estado, el siguiente
      // arranca en 0. Sin rechazos (null) → baseline clásico (L − por-iniciar).
      const statuses = this._GetStatusesForLayer(pictogramLayer)
      const prevSegs = (existing && existing.segments) || []
      const orderMode = this._ResolveStatusOrderMode(pictogramLayer)
      const target = computeAdvanceTarget({ statuses, previousSegments: prevSegs, totalLength, orderMode })
      // Secuencial (strict/capped/rechazos): baseline = frente del estado objetivo.
      // Libre (paralelo): baseline en el origen (cada estado 0→L, el popover elige).
      let appliedMeters = 0
      let completed = false
      if (target) {
        appliedMeters = target.appliedMeters
        completed = target.completed
      }

      return {
        vertices:        entity.vertices,
        totalLength,
        direction:       existing?.direction || 'l2r',
        directionLocked: !!existing,
        appliedMeters,
        // Multi-estado: estado objetivo pre-seleccionado + si el segmento ya concluyó.
        targetStatus:    target ? target.statusValue : null,
        completed,
      }
    },

    /**
     * Arma el registro de avance INLINE sobre un segmento (al doble-click). A partir
     * de aquí el gesto (click inicio A/B + arrastre + popover) opera SOLO sobre este
     * handle; el click simple en otros segmentos sigue mostrando info y el pan/hover
     * siguen igual. Se desarma al aplicar/cancelar el popover o al rearmar otro.
     */
    _ArmInlineAdvance(handle, entity) {
      const ctx = this._GetAdvanceContext(handle, entity)
      if (!ctx) return   // capa no trackeable / oculta / sin ancho de ribbon
      // Cerramos la info si estaba abierta y limpiamos cualquier preview/selección previa.
      this._CloseInfoPopover()
      this._CloseInlinePopover()
      this._inlineAdvance.clearSelection()
      this.inlineArmedHandle = String(handle)
      this.inlineArmed.locked = !!ctx.directionLocked
      this.inlineArmed.completed = !!ctx.completed
      this.inlineArmed.appliedFrac = ctx.totalLength > 0 ? Math.min(1, ctx.appliedMeters / ctx.totalLength) : 0
      // Estado objetivo (Opción A) para pre-seleccionarlo en el popover.
      this._inlineTargetStatus = ctx.targetStatus || null
      // Si el sentido ya está bloqueado (hubo avance), lo reflejamos de entrada
      // (origen fijo) y el nuevo avance continúa desde el frente ya ejecutado.
      if (ctx.directionLocked) {
        this.inlineArmed.selected = ctx.direction
        this._inlineAdvance.selectStart(handle, ctx.direction)
      } else {
        this.inlineArmed.selected = null
      }
      // Mientras esté armado, desactivamos el PAN de OrbitControls (LEFT=PAN) para
      // que el arrastre marque avance en vez de mover el lienzo (el zoom sigue).
      this._SetViewerPanEnabled(false)
      // Dibuja los marcadores A/B y los mantiene pegados al segmento (pan/zoom).
      this._TrackArmedSegment()
      // Escape = salir del modo avance sin aplicar (desarmar). Idempotente.
      // En CAPTURA para que llegue aunque el popover/datepicker consuma el keydown.
      if (this._armedKeyHandler) window.removeEventListener('keydown', this._armedKeyHandler, true)
      this._armedKeyHandler = (e) => { if (e.key === 'Escape') this._DisarmInlineAdvance() }
      window.addEventListener('keydown', this._armedKeyHandler, true)
    },

    /**
     * Clase del marcador A/B: origen elegido = azul relleno; si el sentido está
     * bloqueado, el destino se ve PLANO (gris, no seleccionable); si no, blanco con
     * borde azul (seleccionable).
     */
    _InlineMarkerClass(dir) {
      if (this.inlineArmed.selected === dir) return 'bg-[#3a6bc8] text-white border-[#3a6bc8]'
      if (this.inlineArmed.locked)          return 'bg-gray-100 text-gray-400 border-gray-300'
      return 'bg-white text-[#3a6bc8] border-[#3a6bc8]'
    },

    /**
     * Chevrons de sentido a lo largo del segmento armado (coords de pantalla), en el
     * sentido elegido. Vacío si no hay extremo seleccionado todavía. Mismo criterio
     * que el marcador del modal: 3 flechas a 1/4, 2/4, 3/4 del recorrido.
     */
    _ComputeArmedArrows(pts) {
      if (!pts || pts.length < 2 || !this.inlineArmed.selected) return []
      const seq = this.inlineArmed.selected === 'r2l' ? pts.slice().reverse() : pts
      const cum = [0]
      for (let i = 1; i < seq.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(seq[i].x - seq[i - 1].x, seq[i].y - seq[i - 1].y))
      }
      const total = cum[cum.length - 1]
      if (total < 1e-6) return []
      const arrows = []
      const N = 3
      for (let n = 1; n <= N; n++) {
        const target = total * (n / (N + 1))
        let seg = 1
        while (seg < cum.length && cum[seg] < target) seg++
        if (seg >= cum.length) break
        const segStart = cum[seg - 1]
        const segSpan  = cum[seg] - segStart
        const t = segSpan > 1e-9 ? (target - segStart) / segSpan : 0
        const a = seq[seg - 1]
        const b = seq[seg]
        arrows.push({
          x:     a.x + (b.x - a.x) * t,
          y:     a.y + (b.y - a.y) * t,
          angle: Math.atan2(b.y - a.y, b.x - a.x),
        })
      }
      return arrows
    },

    /** Punto {x,y} a una fracción de arco (0..1) sobre la polilínea de pantalla, en sentido. */
    _ScreenPointAtFrac(pts, direction, frac) {
      if (!pts || pts.length < 2) return null
      const seq = direction === 'r2l' ? pts.slice().reverse() : pts
      const cum = [0]
      for (let i = 1; i < seq.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(seq[i].x - seq[i - 1].x, seq[i].y - seq[i - 1].y))
      }
      const total = cum[cum.length - 1]
      if (total < 1e-6) return null
      const target = Math.max(0, Math.min(frac, 1)) * total
      for (let i = 1; i < seq.length; i++) {
        if (cum[i] >= target) {
          const span = cum[i] - cum[i - 1] || 1e-9
          const t = Math.max(0, Math.min((target - cum[i - 1]) / span, 1))
          const a = seq[i - 1]
          const b = seq[i]
          return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
        }
      }
      const last = seq[seq.length - 1]
      return { x: last.x, y: last.y }
    },

    /** Triángulo (chevron) en coords de pantalla para un arrow {x,y,angle}. */
    _ArrowPoints(arrow) {
      const s = 7
      const cos = Math.cos(arrow.angle)
      const sin = Math.sin(arrow.angle)
      const local = [[s, 0], [-s * 0.6, s * 0.55], [-s * 0.6, -s * 0.55]]
      return local
        .map(p => `${(p[0] * cos - p[1] * sin + arrow.x).toFixed(1)},${(p[0] * sin + p[1] * cos + arrow.y).toFixed(1)}`)
        .join(' ')
    },

    /** rAF: reproyecta los extremos del segmento armado a pantalla (siguen el view). */
    _TrackArmedSegment() {
      cancelAnimationFrame(this._armedRaf)
      const tick = () => {
        if (this.inlineArmedHandle == null) return
        const entity = this._GetEntityByHandle(this.inlineArmedHandle)
        const pts = (entity && this.$refs.refDxfOverlay?.ProjectEntityToScreen?.(entity)) || []
        if (pts.length >= 2) {
          this.inlineArmed.a = pts[0]
          this.inlineArmed.b = pts[pts.length - 1]
          this.inlineArmed.arrows = this._ComputeArmedArrows(pts)
          // Punto de continuación en el frente del avance ya ejecutado (en el
          // sentido elegido). La fracción se conserva bajo la proyección ortográfica.
          if (this.inlineArmed.appliedFrac > 1e-4 && this.inlineArmed.selected) {
            const p = this._ScreenPointAtFrac(pts, this.inlineArmed.selected, this.inlineArmed.appliedFrac)
            if (p) { this.inlineArmed.continue = { visible: true, x: p.x, y: p.y } }
          } else {
            this.inlineArmed.continue.visible = false
          }
          this.inlineArmed.visible = true
        } else {
          this.inlineArmed.visible = false
        }
        this._armedRaf = requestAnimationFrame(tick)
      }
      this._armedRaf = requestAnimationFrame(tick)
    },

    /** Desarma el avance inline: corta el tracker, oculta marcadores y limpia todo. */
    _DisarmInlineAdvance() {
      cancelAnimationFrame(this._armedRaf)
      if (this._armedKeyHandler) {
        window.removeEventListener('keydown', this._armedKeyHandler, true)
        this._armedKeyHandler = null
      }
      this.inlineArmedHandle = null
      this.inlineArmed.visible = false
      this.inlineArmed.selected = null
      this.inlineArmed.arrows = []
      this.inlineArmed.locked = false
      this.inlineArmed.completed = false
      this.inlineArmed.appliedFrac = 0
      this.inlineArmed.continue.visible = false
      // Cerrar también el popover si estaba abierto (p. ej. Esc tras soltar).
      this.inlinePopover.visible = false
      this.inlinePopover.errorMsg = ''
      // Aborta cualquier gesto en curso para que el pointerup no dispare el popover.
      this._inlineAdvance.cancelGesture()
      this._inlineAdvance.clearSelection()
      this._advanceOverlay.clearPreview()
      this.inlineDrag.visible = false
      // Restauramos el pan de OrbitControls.
      this._SetViewerPanEnabled(true)
    },

    /** Activa/desactiva el pan por arrastre (OrbitControls LEFT=PAN) sin tocar el zoom. */
    _SetViewerPanEnabled(enabled) {
      const dxf = this.$refs.cadViewer?.GetDxfViewer?.()
      if (dxf && dxf.controls) dxf.controls.enablePan = enabled
    },

    /**
     * Recibe el gesto del InlineAdvanceController.
     *   - 'preview' (arrastrando): pinta la lane transitoria del delta + chip de metros.
     *   - 'commit' (al soltar con delta): mantiene el preview y abre el popover de
     *     estado/fecha (el apply/persistencia es F4).
     *   - 'select' / 'cancel' / preview-sin-delta: limpia preview + chip.
     */
    _OnInlineGesture(g) {
      // Reflejamos el extremo de inicio activo en los marcadores A/B.
      if (g.direction) this.inlineArmed.selected = g.direction
      switch (g.phase) {
        case 'preview':
          if (this.inlinePopover.visible) this._CloseInlinePopover()
          if (g.deltaMeters > 1e-4) {
            // Entidad canónica del cadJson (tiene el ancho de ribbon; la del hitbox
            // puede venir sin width → lane de 0.1 invisible).
            const previewEntity = this._GetEntityByHandle(g.handle) || g.entity
            this._advanceOverlay.paintPreview(previewEntity, g.direction, g.appliedMeters, g.deltaMeters, INLINE_PREVIEW_COLOR)
            this._UpdateDragViz(previewEntity, g)
          } else {
            this._advanceOverlay.clearPreview()
            this.inlineDrag.visible = false
          }
          break
        case 'commit':
          // El preview (azul) + la visualización del arrastre QUEDAN visibles: así ves
          // el punto donde soltaste y, si quieres aumentar más antes de guardar, puedes
          // arrastrar de nuevo (re-marca absoluto desde el frente). El popover recoge
          // estado + fecha; un nuevo arrastre lo cierra y vuelve a abrirse al soltar.
          this._OpenInlinePopover(g)
          break
        case 'select':
        case 'cancel':
        default:
          this._advanceOverlay.clearPreview()
          this.inlineDrag.visible = false
          break
      }
    },

    /**
     * Actualiza la visualización del arrastre (coords de pantalla): proyecta la
     * entidad, ubica el inicio del tramo (frente de avance) y la punta móvil, traza
     * el recorrido punteado entre ambos y coloca el badge de metros a un COSTADO
     * (offset perpendicular al segmento en la punta → sirve para trazos horizontales,
     * verticales e inclinados) con una guía punteada.
     */
    _UpdateDragViz(entity, g) {
      const pts = this.$refs.refDxfOverlay?.ProjectEntityToScreen?.(entity) || []
      const viz = this._ComputeDragViz(pts, g.direction, this.inlineArmed.appliedFrac || 0, g.frac || 0)
      if (!viz) { this.inlineDrag.visible = false; return }
      this.inlineDrag = {
        visible: true,
        start:   viz.start,
        tip:     viz.tip,
        dash:    viz.dash,
        leader:  viz.leader,
        badge:   { x: viz.badge.x, y: viz.badge.y, text: '+' + Number(g.deltaMeters).toFixed(2) + ' m' },
      }
    },

    /**
     * Geometría de la visualización del arrastre en pantalla. Devuelve inicio, punta,
     * polyline del tramo marcado y badge+guía (offset perpendicular al segmento en la
     * punta, con flip si se saldría por arriba). Null si la polilínea es degenerada.
     */
    _ComputeDragViz(pts, direction, startFrac, endFrac) {
      if (!pts || pts.length < 2) return null
      const seq = direction === 'r2l' ? pts.slice().reverse() : pts
      const cum = [0]
      for (let i = 1; i < seq.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(seq[i].x - seq[i - 1].x, seq[i].y - seq[i - 1].y))
      }
      const total = cum[cum.length - 1]
      if (total < 1e-6) return null
      const at = (frac) => {
        const arc = Math.max(0, Math.min(frac, 1)) * total
        for (let i = 1; i < seq.length; i++) {
          if (cum[i] >= arc) {
            const span = cum[i] - cum[i - 1] || 1e-9
            const t = Math.max(0, Math.min((arc - cum[i - 1]) / span, 1))
            const a = seq[i - 1], b = seq[i]
            return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, i }
          }
        }
        const last = seq[seq.length - 1]
        return { x: last.x, y: last.y, i: seq.length - 1 }
      }
      const s = Math.max(0, Math.min(startFrac, 1))
      const e = Math.max(s, Math.min(endFrac, 1))
      const start = at(s)
      const tip = at(e)
      // Recorrido punteado entre inicio y punta (incluye vértices intermedios).
      const dashPts = [start]
      const sArc = s * total, eArc = e * total
      for (let k = 0; k < seq.length; k++) {
        if (cum[k] > sArc && cum[k] < eArc) dashPts.push(seq[k])
      }
      dashPts.push(tip)
      // Normal al segmento en la punta → badge a un costado, flip si sale arriba.
      // Offset adaptativo: en trazos verticales la normal es horizontal y el badge
      // es ancho → necesita más separación para no montarse sobre el segmento.
      const a = seq[Math.max(1, tip.i) - 1], b = seq[Math.max(1, tip.i)]
      let nx = -(b.y - a.y), ny = (b.x - a.x)
      const len = Math.hypot(nx, ny) || 1
      nx /= len; ny /= len
      const OFF = Math.abs(nx) > Math.abs(ny) ? 50 : 32
      let bx = tip.x + nx * OFF, by = tip.y + ny * OFF
      if (by < 40) { bx = tip.x - nx * OFF; by = tip.y - ny * OFF }
      return {
        start: { x: start.x, y: start.y },
        tip:   { x: tip.x, y: tip.y },
        dash:  dashPts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
        leader: `${tip.x.toFixed(1)},${tip.y.toFixed(1)} ${bx.toFixed(1)},${by.toFixed(1)}`,
        badge: { x: bx, y: by },
      }
    },

    /**
     * Abre el popover de estado/fecha para el delta marcado. Resuelve los estados
     * ELEGIBLES con el mismo helper que el modal (advance-eligibility) y guarda el
     * contexto (handle/entity/totalLength/pictogramLayer/statuses) para el apply (F4).
     */
    _OpenInlinePopover(g) {
      const entity = g.entity
      const pictogramLayer = (this.pictogram?.layers || []).find(l => l.code === entity?.layer)
      if (!pictogramLayer) { this._advanceOverlay.clearPreview(); return }

      const statuses = this._GetStatusesForLayer(pictogramLayer)
      const existing = upgradeAdvanceState(this._advanceState[String(g.handle)])
      const previousSegments = (existing && existing.segments) || []
      const totalLength = (existing && existing.totalLength != null)
        ? Number(existing.totalLength)
        : Math.round(getEntityTotalLength(entity) * 100) / 100
      const orderMode = this._ResolveStatusOrderMode(pictogramLayer)

      const elig = computeStatusEligibility({ statuses, previousSegments, totalLength, orderMode })
      const statusesForSelect = statuses.map(s => {
        const e = elig.get(s.value)
        return { ...s, $isDisabled: !(e && e.eligible) }
      })

      // Contexto para el apply (persistencia F4) — mismo shape que el flujo del modal.
      this._advanceCurrent = { handle: g.handle, entity, totalLength, pictogramLayer, statuses }
      this._inlineGesture  = { direction: g.direction, deltaMeters: g.deltaMeters, totalMeters: g.totalMeters }

      this.inlinePopover = {
        visible:     true,
        position:    { x: g.screen.x, y: g.screen.y },
        statuses:    statusesForSelect,
        direction:   g.direction,
        deltaMeters: g.deltaMeters,
        totalMeters: g.totalMeters,
        totalLength,
        // Estado objetivo (Opción A) a pre-seleccionar; el usuario puede cambiarlo.
        preselect:   this._inlineTargetStatus,
        loading:     false,
        errorMsg:    '',
      }
    },

    /** Cierra el popover inline y limpia el preview + visualización del arrastre. */
    _CloseInlinePopover() {
      this.inlinePopover.visible = false
      this.inlinePopover.errorMsg = ''
      this._advanceOverlay.clearPreview()
      this.inlineDrag.visible = false
    },

    /**
     * Apply del popover inline → MISMA persistencia que el modal (_OnAdvanceApply):
     * POST + clamp de bolsas + paint + rehidratar. En éxito desarma; en error 422
     * (regla de orden de estados) muestra el mensaje en el popover.
     */
    _OnInlinePopoverApply(payload) {
      const g = this._inlineGesture
      if (!g) { this._CloseInlinePopover(); this._DisarmInlineAdvance(); return }
      this.inlinePopover.loading = true
      this.inlinePopover.errorMsg = ''
      this._OnAdvanceApply(
        {
          direction:   g.direction,
          meters:      g.deltaMeters,
          status:      payload.status,
          statusLabel: payload.statusLabel,
          statusColor: payload.statusColor,
          startDate:   payload.startDate,
        },
        {
          onError: (msg) => {
            this.inlinePopover.loading = false
            this.inlinePopover.errorMsg = msg
          },
          onSuccess: () => {
            // Éxito: cerrar popover + desarmar. El rehidratado (dentro de
            // _OnAdvanceApply) repinta el avance persistido del segmento.
            this.inlinePopover.loading = false
            this._CloseInlinePopover()
            this._DisarmInlineAdvance()
          },
        },
      )
    },

    _OnInlinePopoverCancel() {
      this._CloseInlinePopover()
      this._DisarmInlineAdvance()
    },

    /**
     * Resuelve el modo de orden de estados para una capa, con herencia:
     * capa (metadata.status_order_mode) → pictograma
     * (metadata.progress_viewer.status_order_mode) → default 'strict'.
     */
    _ResolveStatusOrderMode(pictogramLayer) {
      const MODES = ['free', 'strict', 'capped']
      const layerMode = pictogramLayer && pictogramLayer.metadata && pictogramLayer.metadata.status_order_mode
      if (MODES.includes(layerMode)) return layerMode
      const picMode = this.pictogram && this.pictogram.metadata
        && this.pictogram.metadata.progress_viewer
        && this.pictogram.metadata.progress_viewer.status_order_mode
      if (MODES.includes(picMode)) return picMode
      return 'strict'
    },

    /**
     * Devuelve los statuses disponibles para registrar avance sobre una capa.
     * Formato uniforme: `[{ value, label, color }]`.
     *
     *   - Si la capa tiene `statuses` configurados desde BD (vienen filtrados
     *     por type='status' gracias al modelo PictogramLayer): los mapeamos
     *     usando `id` como value, `name` como label y `color` como color real.
     *   - Si no: usamos el array hardcoded ADVANCE_STATUSES con labels i18n.
     */
    _GetStatusesForLayer(pictogramLayer) {
      const raw = pictogramLayer?.statuses
      if (Array.isArray(raw) && raw.length > 0) {
        return raw.map(s => ({
          value: s.id,
          label: s.name,
          color: s.color || '#9ca3af',
          // `weight` define el signo del estado: < 0 = rechazo (rama negativa),
          // >= 0 o null = paso positivo. Lo usa RegisterAdvanceModal.statusEligibility
          // para resolver el predecesor real (el positivo anterior, saltando rechazos).
          weight: s.weight != null ? Number(s.weight) : null,
        }))
      }
      // Fallback hardcoded — convertimos el color int a CSS hex.
      return ADVANCE_STATUSES.map(s => ({
        value: s.value,
        label: this.$t('advance_status_' + s.value),
        color: '#' + s.color.toString(16).padStart(6, '0'),
      }))
    },

    /**
     * Handler del evento epc-apply de RegisterAdvanceModal.
     * El modelo es acumulativo por segmentos: cada Apply agrega un nuevo
     * tramo (no modifica los existentes).
     *
     * @param {Object} payload
     * @param {String} payload.direction   — 'l2r' | 'r2l' (solo se setea la
     *                                       primera vez; las siguientes ignoradas)
     * @param {Number} payload.meters      — DELTA: metros nuevos en este registro
     * @param {*}      payload.status      — value del status (id numérico o string)
     * @param {String} payload.statusLabel — label legible para tooltip/breakdown
     * @param {String} payload.statusColor — color hex para pintar el tramo
     */
    async _OnAdvanceApply({ direction, meters, status, statusLabel, statusColor, startDate }, opts = {}) {
      // Callbacks de error/éxito: por defecto el modal; el gesto inline inyecta los
      // suyos (popover) para reutilizar TODO el flujo (clamp bolsas → POST → persist
      // → paint → rehidratar) sin duplicarlo.
      const onError   = opts.onError   || ((msg) => this.$refs.refRegisterAdvanceModal?.setError?.(msg))
      const onSuccess = opts.onSuccess || (() => this.$refs.refRegisterAdvanceModal?.hide?.())
      const current = this._advanceCurrent
      if (!current) return
      const { totalLength, pictogramLayer } = current
      const delta = Math.max(0, Number(meters) || 0)
      if (delta <= 0) return
      const key = String(current.handle)

      // Obtener (o inicializar) el state acumulativo del handle.
      let state = upgradeAdvanceState(this._advanceState[key])
      if (!state) {
        state = {
          direction,
          totalLength,
          segments: [],
        }
      }

      // Clampear el delta (red de seguridad; el modal ya limita al mismo tope).
      // En capas CON rechazos: MODELO DE BOLSAS (flujo parcial con retorno). El tope
      // de cada acción sale de la bolsa que consume: Ejecutar = por-iniciar+rechazado;
      // Aceptar/Rechazar del nivel k = bolsa del nivel anterior (bags[k-1]). Ver
      // simulatePools (advance-pool.helper). En capas sin rechazos (clásico), cada estado es un proceso
      // paralelo: no exceder totalLength menos lo acumulado.
      const hasRejects = (current.statuses || []).some(s => Number(s.weight) < 0)
      const isRejectStatus = (v) => {
        const s = (current.statuses || []).find(x => x.value === v)
        return s && Number(s.weight) < 0
      }
      let clamped
      if (hasRejects) {
        const pools = simulatePools(state.segments, current.statuses, totalLength)
        const idx = (current.statuses || []).findIndex(s => s.value === status)
        let cap = 0
        if (idx >= 0) {
          if (isRejectStatus(status)) {
            // Rechazo: consume de la bolsa del nivel anterior a su accept emparejado.
            const acc = pools.prevIdx[idx]
            const ka = (acc >= 0 && pools.posLevelOfIdx[acc] !== undefined) ? pools.posLevelOfIdx[acc] : -1
            cap = (ka >= 1) ? (pools.bags[ka - 1] || 0) : 0
          } else {
            const pk = pools.posLevelOfIdx[idx]
            cap = (pk === 0) ? ((pools.unstarted || 0) + (pools.rejected || 0)) : (pools.bags[pk - 1] || 0)
          }
        }
        clamped = Math.min(delta, Math.max(0, cap))
      } else {
        const usedForStatus = state.segments
          .filter(s => s.status === status)
          .reduce((acc, s) => acc + (s.meters || 0), 0)
        clamped = Math.min(delta, Math.max(0, totalLength - usedForStatus))
      }
      if (clamped <= 0) return

      // Resolver status_id real (solo si es numérico — viene de
      // pictogram_layer_statuses). Cuando es string ('in_progress', etc.)
      // estamos en fallback hardcoded → mandamos null al backend y el
      // label/color caché lo permite mostrar igualmente.
      const statusId = typeof status === 'number' ? status : null

      // 1. POST al backend PRIMERO. Solo si éxito persistimos local y pintamos.
      if (this.pictogram?.id && pictogramLayer?.id) {
        try {
          await registerLayerProgress(this.pictogram.id, pictogramLayer.id, {
            dxf_handle:        key,
            total_length:      totalLength,
            direction:         state.direction,
            executed_quantity: clamped,
            status_id:         statusId,
            start_date:        startDate,
          })
        } catch (err) {
          logger.error('registerLayerProgress failed:', err)
          // Si el backend rechazó por la regla de orden de estados (422 de
          // ValidationException), mostramos su mensaje específico; sino, genérico.
          const data = err && err.response && err.response.data
          const backendMsg = (data && data.errors && data.errors.status_id && data.errors.status_id[0])
            || (data && data.message)
          onError(backendMsg || this.$t('error_saving_progress'))
          return  // Sin éxito → no pintamos ni cacheamos.
        }
      }

      // 2. Persistencia local (cache transitoria de la sesión) + paint.
      state.segments.push({
        meters:      clamped,
        status,
        statusLabel,
        statusColor,
        createdAt:   Date.now(),
      })
      this._advanceState[key] = state
      persistAdvanceState(this.uploadedFile, this._advanceState)

      // Fuente de entity: el del cadJson por handle (misma que usa el
      // rehidratado). `current.entity` (raycast del overlay) queda de fallback.
      const paintEntity = this._GetEntityByHandle(current.handle) || current.entity

      this._PaintAdvanceForEntity(current.handle, paintEntity, state.direction, state.segments)
      // Si hay un resaltado activo, este handle pudo cambiar de grupo
      // (pasó de "sin avance" a "con avance") — recalculamos el resaltado.
      if (this.highlightMode) this._ApplyHighlight()
      // 3. Cerrar (modal o popover inline) solo después del paint exitoso.
      onSuccess()

      // 4. Re-hidratar desde el backend — MISMO camino que el refresh (que SÍ se
      // visualiza). registerLayerProgress persistió el nuevo estado; volvemos a
      // consultar getPictogramComponents (estado canónico de cada segmento) y
      // re-pintamos todo. Ventaja extra: al ser async, el re-pintado ocurre tras
      // el fetch, con el modal ya cerrado y el layout asentado (el "buen momento"
      // de render que en el reload ocurre solo).
      this._RehydrateAdvance()
    },

    /**
     * Estado ACTUAL a pintar de una polilínea con flujo de rechazos: el ÚLTIMO
     * estado registrado cronológicamente, pintado sobre sus METROS NETOS reales
     * (el avance es parcial y acumulativo: 10 + 20 = 30). Un RECHAZO rechaza TODO
     * → resetea el neto a 0; pero SÍ se pinta con el COLOR DEL RECHAZO sobre los
     * metros que fueron rechazados (el neto que había justo antes del rechazo; al
     * 100% = todo el tramo). Al registrar un nuevo avance después, el neto arranca
     * de 0 y el color positivo nuevo reemplaza al del rechazo. Si aún no hay avance
     * neto → []. Nota: el reset del neto para el TOOLTIP (_OnAdvanceHover) y la
     * ELEGIBILIDAD (statusEligibility) sigue igual — esta función solo pinta.
     */
    /**
     * Pinta un THREE.Group con quads translúcidos sobre los primeros `meters`
     * de la polilínea, partiendo del extremo elegido. Reemplaza el overlay
     * anterior si existía para el mismo handle.
     */
    /**
     * Pinta el avance acumulado sobre la polilínea como CARRILES PARALELOS,
     * uno por status. Cada status es un proceso independiente que arranca
     * desde el extremo de inicio (según direction):
     *
     *   "Cavar"  → 50m desde el extremo A (carril superior)
     *   "Tapar"  → 15m desde el extremo A (carril central)
     *   "Fibra"  → 10m desde el extremo A (carril inferior)
     *
     * El ancho del ribbon se divide proporcionalmente entre N status.
     * Si solo hay 1 status, ocupa todo el ancho.
     */
    _PaintAdvanceForEntity(handle, entity, direction, segments) {
      // Delega el render al AdvanceOverlay (engine-adjacent). El componente solo
      // inyecta los accessors de datos (deps) y el estado de dim actual.
      this._advanceOverlay.paintEntity(handle, entity, direction, segments, {
        getStatusesForEntity: (ent) => {
          const layer = (this.pictogram?.layers || []).find(l => l.code === ent?.layer)
          return layer ? this._GetStatusesForLayer(layer) : []
        },
        filterSegmentsForView: (segs) => this._FilterSegmentsForView(segs),
        resolveStatusColor:    (item) => this._ResolveStatusColor(item),
        activeLayer:           this.activeLayerCode,
        highlightOn:           !!this.highlightMode,
      })
    },

    /**
     * Resuelve el color del status para un segment.
     *   1. Si hay statusColor (hex) en el segment, usar eso.
     *   2. Sino, fallback a ADVANCE_STATUSES hardcoded por value.
     */
    _ResolveStatusColor(segment) {
      if (segment?.statusColor) {
        return parseInt(String(segment.statusColor).replace('#', ''), 16) || 0x00b894
      }
      const def = ADVANCE_STATUSES.find(s => s.value === segment?.status) || ADVANCE_STATUSES[1]
      return def.color
    },

    _DisposeAdvanceMesh(handle) {
      this._advanceOverlay.disposeEntity(handle)
    },

    _DisposeAllAdvanceMeshes() {
      this._advanceOverlay.disposeAll()
    },

    // ─── Filtro de visualización de estados (dropdown "Ver avance") ───────

    /**
     * Handler del dropdown "Ver avance". `option` es { value, ... }:
     *   'last' | 'all' | <statusValue>. Cambia el modo y repinta todo el
     *   avance con el filtro nuevo.
     */
    _OnStatusViewSelected(option) {
      const v = option && option.value
      if (v == null) return
      this.statusViewMode = v
      this._RepaintAllAdvance()
    },

    /**
     * Repinta todos los avances aplicando el filtro de estados actual.
     * _PaintAdvanceForEntity ya descarta el mesh previo de cada handle, así
     * que basta con recorrer el state y repintar.
     */
    _RepaintAllAdvance() {
      this._PaintAllAdvance()
    },

    /**
     * Filtra los segmentos a pintar según `statusViewMode`:
     *   - 'all'  → todos.
     *   - 'last' → solo los del ÚLTIMO estado registrado (el segmento con
     *     mayor createdAt define el estado; se pintan todos los de ese estado).
     *   - <value> → solo los de ese estado puntual.
     */
    _FilterSegmentsForView(segments) {
      if (!Array.isArray(segments) || segments.length === 0) return segments
      const mode = this.statusViewMode || 'last'
      if (mode === 'all') return segments
      if (mode === 'last') {
        let last = segments[0]
        for (const s of segments) {
          if ((s.createdAt || 0) >= (last.createdAt || 0)) last = s
        }
        return segments.filter(s => s.status === last.status)
      }
      // Estado puntual — comparamos como string para tolerar id numérico o
      // value string del fallback hardcoded.
      return segments.filter(s => String(s.status) === String(mode))
    },

    // ─── Resaltado del footer (dropdown "Resaltar") ───────────────────────
    //
    // Dos filtros visuales de un vistazo sobre las polilíneas trackeables
    // (width > 0 y capa que matchea un pictogram_layer):
    //   - 'without': color uniforme sobre las que NO tienen avance (pendientes)
    //   - 'with':    color uniforme sobre las que SÍ tienen avance
    // Es toggle: reelegir el modo activo lo apaga. Los meshes viven en el
    // HighlightOverlay (aparte de los del avance) para no interferir con los
    // carriles de avance ni con el dim por capa.

    /**
     * Handler del dropdown "Resaltar". `option` es la opción emitida por
     * SplitActionDropdown ({ value, label, ... }). Toggle: si el modo elegido
     * ya está activo, se apaga; si no, se aplica el nuevo.
     */
    _OnHighlightSelected(option) {
      const mode = option && option.value
      if (mode !== 'without' && mode !== 'with') return
      if (this.highlightMode === mode) {
        this._ClearHighlight()
        return
      }
      this.highlightMode = mode
      this._ApplyHighlight()
    },

    /** Apaga el resaltado y descarta sus meshes; restaura el gris por capa. */
    _ClearHighlight() {
      this.highlightMode = null
      this._DisposeAllHighlightMeshes()
      // Vuelve al estado normal: la capa activa recupera color, las no-activas
      // quedan en gris (y ya no "todo gris").
      this._ApplyLayerDim()
    },

    /**
     * (Re)pinta el resaltado según `highlightMode`. Primero recalcula el gris
     * de toda la escena (con resaltado activo TODO va a gris vía _ApplyLayerDim)
     * y ENCIMA pinta un ribbon de color uniforme sobre las polilíneas que
     * cumplen el filtro (con/sin avance). Idempotente: descarta lo anterior.
     */
    _ApplyHighlight() {
      this._DisposeAllHighlightMeshes()
      // Poner todo en gris (o restaurar) según el modo actual, ANTES de pintar
      // el resaltado encima. Así el color de resaltado es lo único que resalta.
      this._ApplyLayerDim()
      const mode = this.highlightMode
      if (!mode) return
      const dxf = this.$refs.cadViewer?.GetDxfViewer?.()
      if (!dxf?.HasRenderer?.()) return

      const colorInt     = mode === 'with' ? HIGHLIGHT_COLORS.with : HIGHLIGHT_COLORS.without
      const wantProgress = mode === 'with'

      for (const entity of this._GetTrackableEntities()) {
        const handle = String(entity.handle)
        if (this._EntityHasProgress(handle) !== wantProgress) continue
        this._PaintHighlightForEntity(handle, entity, colorInt)
      }
      dxf.Render?.()
    },

    /**
     * Polilíneas trackeables del diseño actual: POLYLINE/LWPOLYLINE con
     * width > 0 cuya capa (code) matchea un pictogram_layer. Mismo criterio
     * que usa el overlay de clicks para permitir registrar avance.
     */
    _GetTrackableEntities() {
      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return []
      const raw  = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      const layerCodes = new Set((this.pictogram?.layers || []).map(l => l.code))
      const out = []
      for (const e of list) {
        if (!e || (e.type !== 'POLYLINE' && e.type !== 'LWPOLYLINE')) continue
        if (getEntityMaxWidth(e) <= 0) continue
        if (!layerCodes.has(e.layer)) continue
        out.push(e)
      }
      return out
    },

    /**
     * Busca una entity del cadJson por su handle. Fuente única y fiable para
     * resolver la longitud total de una polilínea (independiente de qué mesh
     * haya golpeado el raycaster del hover).
     */
    _GetEntityByHandle(handle) {
      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return null
      const raw  = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      const h = String(handle)
      for (const e of list) {
        if (e && String(e.handle) === h) return e
      }
      return null
    },

    /** True si el handle tiene al menos un segmento de avance con metros > 0. */
    _EntityHasProgress(handle) {
      const state = this._advanceState?.[String(handle)]
      if (!state || !Array.isArray(state.segments)) return false
      return state.segments.some(s => (s.meters || 0) > 0)
    },

    /** Pinta el resaltado de una entidad (delegado al HighlightOverlay). */
    _PaintHighlightForEntity(handle, entity, colorInt) {
      this._highlightOverlay.paintEntity(handle, entity, colorInt)
    },

    _DisposeAllHighlightMeshes() {
      this._highlightOverlay.disposeAll()
    },

    // ─── Persistencia en localStorage ─────────────────────────────────────
    // La lógica vive en advance-storage.helper (loadAdvanceState /
    // persistAdvanceState); aquí solo se invoca con uploadedFile + _advanceState.

    /**
     * Re-pinta todos los avances guardados para el archivo actual.
     * Se llama tras onSceneLoaded — necesita el viewer ya inicializado
     * y el cadJson disponible para resolver entity por handle.
     *
     * Orden de hidratación:
     *   1. localStorage (cache rápido por archivo) — pinta de inmediato.
     *   2. Backend (canonical) — Component Mongo del pictograma. Si la
     *      respuesta llega y difiere, reescribe el state y re-pinta.
     *   Backend gana siempre que esté disponible. Si el GET falla, queda
     *   lo que había en localStorage (degrada graciosamente).
     */
    async _RehydrateAdvance() {
      this._advanceState = loadAdvanceState(this.uploadedFile)
      this._PaintAllAdvance()

      // Solo electrical/trenches tienen este endpoint.
      if (!this.pictogram?.id) return
      try {
        // apiCall (helpers/api.helper.js) ya unwrapea axios → retorna response.data directo.
        const data = await getPictogramComponents(this.pictogram.id) || {}
        if (!data.success) return

        const statuses = data.statuses || {}
        const components = Array.isArray(data.components) ? data.components : []

        const next = Object.create(null)
        for (const comp of components) {
          const handle = comp.handle != null ? String(comp.handle) : null
          if (!handle) continue
          const segments = (comp.segments || []).map((seg) => {
            // Mantenemos el mismo shape que produce _OnAdvanceApply:
            //   - `status` es el value (id numérico) — sirve de key para
            //     agrupar en _PaintAdvanceForEntity por status idéntico.
            //   - `statusLabel` / `statusColor` se resuelven contra el
            //     diccionario `statuses` indexado por id.
            const statusObj = statuses[seg.status_id] || null
            return {
              meters:      Number(seg.meters) || 0,
              status:      seg.status_id,
              statusLabel: statusObj ? statusObj.name : null,
              statusColor: statusObj ? statusObj.color : null,
              createdAt:   seg.created_at ? new Date(seg.created_at).getTime() : Date.now(),
              lineId:      seg.line_id ?? null,
              startDate:   seg.start_date ?? null,
            }
          })
          next[handle] = {
            direction: comp.direction || 'l2r',
            // Persistimos totalLength para que tooltips/cálculos no dependan
            // del mesh golpeado. Preferimos el que envía el backend; si no,
            // lo resolvemos desde la entity del cadJson por handle.
            // Redondeado a 2 decimales (precisión de los metros registrados) para
            // que la elegibilidad de estados detecte el 100% exacto (cap == 0).
            totalLength: comp.total_length != null
              ? Math.round(Number(comp.total_length) * 100) / 100
              : Math.round(getEntityTotalLength(this._GetEntityByHandle(handle) || {}) * 100) / 100,
            segments,
          }
        }

        // Limpieza: el primer paint pudo dejar meshes para handles que solo
        // estaban en localStorage. Reseteamos antes de re-pintar desde
        // backend (canonical).
        this._DisposeAllAdvanceMeshes()
        this._advanceState = next
        persistAdvanceState(this.uploadedFile, this._advanceState)
        this._PaintAllAdvance()
      } catch (e) {
        logger.warn('Hidratación de avance desde backend falló — uso localStorage:', e)
      }
    },

    /**
     * Pinta todos los handles del _advanceState actual sobre el viewer.
     * Resuelve cada handle a una entity del cadJson y delega en
     * _PaintAdvanceForEntity. Handles sin entity correspondiente se saltan.
     */
    _PaintAllAdvance() {
      const handles = Object.keys(this._advanceState || {})
      if (handles.length === 0) return

      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return
      const raw = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      const entityByHandle = Object.create(null)
      for (const e of list) {
        if (e?.handle != null) entityByHandle[String(e.handle)] = e
      }

      for (const handle of handles) {
        const entity = entityByHandle[handle]
        if (!entity) continue
        const state = upgradeAdvanceState(this._advanceState[handle])
        if (!state) continue
        this._advanceState[handle] = state
        this._PaintAdvanceForEntity(handle, entity, state.direction, state.segments)
      }

      // Los grupos recién pintados nacen visibles → re-aplicar la visibilidad de
      // capa para no re-mostrar carriles de capas ocultas tras un repintado.
      for (const name in this.layerVisibility) {
        if (this.layerVisibility[name] === false) this._advanceOverlay.setLayerVisible(name, false)
      }
    },

    // ─── Tooltip al pasar el mouse sobre un avance pintado ────────────────
    //
    // Listener pointermove en el canvas + raycaster propio (no usamos el del
    // overlay porque el del overlay ya consume pointerdown y filtramos por
    // grupo distinto). Hit sobre _advanceMeshes → mostramos floating div.

    _BindHoverTooltip() {
      this._hoverTooltip.bind()
    },

    _UnbindHoverTooltip() {
      this._hoverTooltip.unbind()
    },

    _HideTooltip() {
      if (this.advanceTooltip.visible) this.advanceTooltip.visible = false
    },

    // Targets del hit-test: carriles de avance (del overlay) + hitboxes del
    // overlay interactivo (cubre la polilínea trackeable "sin avance todavía").
    // En modo PAN no hay hover → sin targets, el controller oculta el tooltip.
    _HoverTargets() {
      if (this.isDraggable) return []
      const targets = []
      const advanceGroups = this._advanceOverlay.getGroups()
      for (const h of Object.keys(advanceGroups)) {
        const g = advanceGroups[h]
        if (g) targets.push(g)
      }
      const overlayGroup = this.$refs.refDxfOverlay?.GetGroup?.()
      if (overlayGroup) targets.push(overlayGroup)
      return targets
    },

    // Resolución de negocio del tooltip para un handle (longitud + breakdown).
    _ResolveHoverTooltip(handle, pickedEntity) {
      const data = this._advanceState[handle]
      // Longitud total: SIEMPRE desde la entity por handle (cadJson); fallback a
      // la entity del hit (los hitboxes del overlay la llevan en userData). Así un
      // carril pintado (que no lleva entity) también muestra la longitud correcta.
      const entity = this._GetEntityByHandle(handle) || pickedEntity || {}
      // No mostrar tooltip de una capa oculta (el hitbox puede seguir
      // intersectándose aunque el carril esté oculto).
      if (this.layerVisibility[entity?.layer] === false) return null
      const totalLength = (data && data.totalLength)
        || getEntityTotalLength(entity)
        || 0

      // Breakdown (modelo de bolsas si la capa tiene rechazos; suma por estado si no).
      const layer = (this.pictogram?.layers || []).find(l => l.code === entity?.layer)
      const layerStatuses = layer ? this._GetStatusesForLayer(layer) : []
      const breakdown = computeHoverBreakdown(data, layerStatuses, totalLength, {
        pending:  this.$t('pending_execution'),
        rejected: this.$t('rejected'),
      })
      const totalMeters = breakdown.reduce((acc, b) => acc + (b.meters || 0), 0)

      return { totalLength, totalMeters, breakdown }
    },

    _ShowTooltip({ x, y, totalLength, totalMeters, breakdown }) {
      this.advanceTooltip.visible     = true
      this.advanceTooltip.x           = x
      this.advanceTooltip.y           = y
      this.advanceTooltip.totalLength = totalLength
      this.advanceTooltip.totalMeters = totalMeters
      this.advanceTooltip.breakdown   = breakdown
    },

    onError(message) {
      logger.error('viewer error:', message)
      this.$toast.open({
        message,
        type: 'is-danger',
        position: 'is-top-right',
      })
    },

    /**
     * _GetLayers retorna los layers que hacen referencia a los layer manejador por nosotros, no a los que estan definidos en el
     * DXF de momento lo harcodeamos, porque son las que por defecto deben crearse y generarse, pero en el futuro hay que retocar
     * esta parte para adicionar otros dinamicamente
     * Propiedad create_entities indica si se van a crear las entidades al momento de generar el parser del DXF, ademas puede usarse
     * para identificar si se inserta en la tabla pictogram_layers (Si es muy ambiguo crear la propiedad adecuada y ajustar)
     * Propiedad in_design indica que viene en el diseño y no hay la necesidad de crear las entidades
     * Propiedad icon es el icono que representara el layer por defecto seria null
     */
     _GetLayers() {
      return getLayerDescriptors({
        totalEntitiesDesign: this.totalEntitiesDesign,
        totalStructures:     this.total_structures,
        createElements:      this.createElements,
      })
    },

    /**
     * Descriptores de capa para electrical/trenches construidos desde la selección
     * hecha en SelectLayersModal. Mismo schema que getLayerDescriptors() para
     * mantener compat con savePictogram y PictogramService::addLayer del backend,
     * más el campo `length` (metros) que se persistirá en pictogram_layers.length.
     *
     * `code` = nombre de la capa DXF original (sirve como identificador único
     * dentro del pictograma). `total_entities` = conteo de entidades de la capa.
     */
    _GetSelectedLayerDescriptors() {
      const selSet = new Set(this.selectedLayerCodes)
      // Convención por tipo de pictograma — todas las capas del mismo
      // pictograma comparten icono y comparten un prefijo numerado de code:
      //   electrical_installation → icon=bolt, code=electrical_1..n
      //   electrical_trenches     → icon=road, code=trench_1..n
      // El prefijo numerado normaliza el `code` (el nombre técnico de la capa
      // DXF puede ser cualquier cosa: "ELECTRICAL_HV_LINE", "Layer_42", etc.)
      // y deja `name` libre para el rename humano del usuario.
      // Convención (icono + prefijo de code) del perfil del tipo.
      const conv = {
        icon:       this.profile.design.layerIcon || null,
        codePrefix: this.profile.design.codePrefix || 'layer',
      }

      return this.dxfLayersInfo
        .filter(l => selSet.has(l.name))
        .map((l, i) => ({
          // `code` = identificador normalizado dentro del pictograma (1-based).
          // `name` = lo que verá el usuario; respeta el rename hecho en
          // SelectLayersModal si existe, sino cae al nombre DXF original.
          // `dxf_layer` = nombre técnico de la capa en el DXF (lo usa el
          // encoder binario para mapear entidades a su code normalizado;
          // el backend lo ignora por ahora pero queda en payload por si
          // hace falta el mapping a futuro).
          code:            `${conv.codePrefix}_${i + 1}`,
          name:            this.customLayerNames[l.name] || l.displayName || l.name,
          icon:            conv.icon,
          // Color RGB (0x00RRGGBB) de la capa DXF — el backend lo persiste en
          // pictogram_layers.color y lo replica en el design doc para teñir el
          // icono de capa en la config de BOQ links (igual que mechanical).
          color:           l.color != null ? l.color : null,
          dxf_layer:       l.name,
          order:           i,
          quantity:        l.entityCount,
          total_entities:  l.entityCount,
          // Redondeo a 2 decimales — el backend lo guardará como decimal(14,2).
          length:          Number((l.lengthMeters || 0).toFixed(2)),
          create_entities: true,
          in_design:       true,
        }))
    },

    /**
     * Construye el .epcb binario (Float32 zero-copy) para electrical/trenches
     * y lo sube a S3 vía uppy. Devuelve el `awsResponse` que el caller pasa
     * al `savePictogram` como `sceneAws` — el service lo persiste igual que
     * el JSON del mecánico, pero apuntando al binario.
     *
     * IMPORTANTE: el .epcb incluye TODAS las capas del DXF — es el snapshot
     * visual del diseño completo. La selección del SelectLayersModal tiene
     * otro propósito (filas en `pictogram_layers` para registrar avance);
     * son cosas distintas y deben mantenerse separadas. Si el .epcb tuviera
     * solo las capas seleccionadas, el cliente vería un dibujo recortado.
     *
     * Cada capa va con su nombre DXF original como `code` (no el numerado
     * `electrical_n` que usan los pictogram_layers); eso mantiene el .epcb
     * agnóstico del flow de tracking.
     */
    async _UploadBinaryScene(selectedDescriptors = []) {
      // CRÍTICO: NO usar `this.dxfLayersInfo` para construir el .epcb.
      //
      // `dxfLayersInfo` se arma con un filtro adicional (getTextLayers) que
      // descarta cualquier capa con TEXT/MTEXT o INSERTs a blocks con texto.
      // Ese filtro tiene sentido para el modal de selección (no se ofrece
      // "capa de etiquetas" como trackeable) pero NO para el render: una capa
      // con polilíneas + 1 etiqueta de texto debe igual ir al .epcb con sus
      // polilíneas. El encoder a nivel de entidad ya descarta los TEXT/MTEXT.
      //
      // Por eso consultamos el viewer directamente con GetLayers(true) — todas
      // las capas no-vacías (al menos una entidad renderizada), sin filtro.
      const viewerLayers = this.$refs.cadViewer?.GetLayers?.(true) || []
      if (viewerLayers.length === 0) {
        throw new Error('layers_not_built')
      }

      // Mapping `dxf_layer → pictogram_layer.code` para las capas seleccionadas.
      // Esto permite que en el .epcb las polilíneas trackeables vayan con el
      // code numerado (electrical_n / trench_n) — el mismo que se persiste en
      // pictogram_layers — así el cliente puede cruzarlas en O(1) con un
      // `pictogram.layers.find(l => l.code === entity.layer)`.
      //
      // Las capas NO seleccionadas siguen con el nombre DXF original: van al
      // .epcb como contexto visual del diseño completo, pero no son
      // clickeables para registrar avance (no matchean con pictogram_layers).
      const codeByDxfLayer = Object.create(null)
      for (const d of selectedDescriptors) {
        if (d.dxf_layer) codeByDxfLayer[d.dxf_layer] = d.code
      }

      const encoderLayers = viewerLayers.map(l => ({
        dxfLayer: l.name,
        code:     codeByDxfLayer[l.name] || l.name,
        color:    l.color,
      }))

      const buf = encodeScene(this.cadJson, { layers: encoderLayers })
      logger.info(
        'binary scene encoded:', buf.byteLength, 'bytes /',
        encoderLayers.length, 'viewer layers (',
        selectedDescriptors.length, 'trackable)'
      )

      // Nombre paralelo al DXF original (cambiamos la extensión a .epcb).
      const baseName = (this.uploadedFile?.name || 'pictogram').replace(/\.[^.]+$/, '')
      const file = new File([buf], baseName + '.epcb', {
        type: 'application/octet-stream',
      })
      return await uploadFile(file)
    },

    async _OnConfigureSubdivisionsApply({ cadLayeredDesign, createElements }) {
      this.cadLayeredDesign = cadLayeredDesign
      this.createElements   = createElements

      // Fase 1 — "Preparando procesador..."
      this.ShowLoader({ loading: true, text: this.$t('preparing_processor') })

      // Estado para calcular ETA durante el progreso
      const startTime = performance.now()
      // Umbral por encima del cual se muestra el aviso "no cierres la página"
      const WARN_THRESHOLD = 500

      try {
        const { totalEntitiesDesign, processedCadJson } = await processSubdivisions({
          cadJson:          this.cadJson,
          layers:           this._GetLayers(),
          cadLayeredDesign,
          onProgress: ({ processed, total, percent }) => {
            // Fase 2 — "Procesando subdivisiones X/Y (Z%)" + ETA + warning condicional
            const lines = [this.$t('processing_subdivisions_progress', [processed, total, percent])]
            // ETA: a partir del 5% el cálculo se estabiliza
            if (percent >= 5 && percent < 100) {
              const elapsedMs = performance.now() - startTime
              const remainingMs = (elapsedMs / processed) * (total - processed)
              lines[0] += ' · ' + this.$t('eta_seconds', [Math.round(remainingMs / 1000)])
            }
            // Aviso para procesos largos
            if (total > WARN_THRESHOLD) {
              lines.push(this.$t('do_not_close_page'))
            }
            this.ShowLoader({ loading: true, text: lines.join('\n') })
          },
        })

        // Fase 3 — "Construyendo escena 3D..."
        // Damos 2 frames al browser para que pinte este texto ANTES de asignar
        // processedCadJson (que dispara el watcher del Viewer y DxfScene.Build,
        // que sí bloquea el main thread por varios segundos).
        this.ShowLoader({ loading: true, text: this.$t('building_scene_3d') })
        await this.$nextTick()
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))

        this.totalEntitiesDesign = totalEntitiesDesign
        this.processedCadJson    = processedCadJson
        // La vista por defecto se aplica en `onSceneLoaded` (evento epc-loaded del Viewer),
        // antes del primer paint, para evitar el flash de "todas las layers visibles".
      } catch (err) {
        this.$toast.open({
          message:  err.message || this.$t('error_processing'),
          type:     'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.ShowLoader({ loading: false, text: null })
      }
    },

    async _OnSavePictogramApply({ name, code, type }) {
      try {
        // El modal no propaga el tipo: el componente lo conoce por prop.
        // Sin esto el service caería al default MECHANICAL y dispararía
        // el worker de scene (errores tipo "blockCtx is not defined").
        const effectiveType = type || this.typePictogram
        // Perfil del tipo efectivo (el modal puede propagar un type distinto al prop).
        const design = getProfile(effectiveType).design

        // Validación específica para select-layers (electrical/trenches): el usuario
        // debe haber marcado al menos una capa en el footer. Sin capas no hay nada
        // que registrar en pictogram_layers y el guardado pierde sentido.
        const layersPayload = design.loadFlow === 'subdivisions'
          ? this._GetLayers()
          : this._GetSelectedLayerDescriptors()
        if (design.loadFlow === 'select-layers' && layersPayload.length === 0) {
          this.$toast.open({
            message:  this.$t('select_at_least_one_layer'),
            type:     'is-warning',
            position: 'is-top-right',
          })
          return
        }

        // Para electrical/trenches generamos el scene binario .epcb y lo
        // subimos antes del POST al pictograma. El service `savePictogram`
        // ya está preparado para aceptar `sceneAws` precocido y propagarlo
        // al backend en `aws_response`.
        let sceneAws = null
        if (design.generatesBinaryScene) {
          this.ShowLoader({ loading: true, text: this.$t('uploading_scene') })
          // Pasamos los descriptors de capas seleccionadas para que el
          // encoder mapee sus polilíneas con el code numerado del
          // pictogram_layer (electrical_n / trench_n). Las no seleccionadas
          // siguen con el nombre DXF original como code en el .epcb.
          sceneAws = await this._UploadBinaryScene(layersPayload)
          if (!sceneAws) throw new Error('upload_scene_error')
        }

        // Capturas:
        //   - mechanical_installation: una por cada vista (pilings/structures/…)
        //     porque el viewer-konva las usa para previsualizar progreso por capa.
        //   - electrical/trenches: una sola captura general que sirve como
        //     preview del pictograma en listados (no hay vistas configurables).
        this.ShowLoader({ loading: true, text: this.$t('generating_screenshots') })
        this.showPanelScreenshot = true
        let screenshots = []
        if (design.captureMode === 'by-views') {
          screenshots = await captureViewerByViews({
            viewer:     this.$refs.cadViewer,
            views:      this.viewsList,
            applyView:  (c) => this._ApplyDisplayView(c),
            // Mismo fit robusto que la vista en vivo (ignora outliers XREF).
            fit:        () => this._OnFitContent(),
            onPreview:  (url) => { this.screenshot = url },
          })
          // Restaurar vista por defecto tras capturar.
          this._ApplyDisplayView(this.displayView)
        } else {
          // Nombre del archivo desde el perfil (electrical.png / trench.png /
          // preview.png) para que la tabla de pictogramas lo busque consistente.
          screenshots = await captureViewerSnapshot({
            viewer:    this.$refs.cadViewer,
            // Mismo fit robusto que la vista en vivo (ignora outliers XREF que
            // dejaban la captura como un punto magenta diminuto).
            fit:       () => this._OnFitContent(),
            onPreview: (url) => { this.screenshot = url },
            name:      design.screenshotName || 'preview.png',
          })
        }

        const pictogram = await savePictogram({
          entityId:         this.entityId,
          dxfFile:          this.uploadedFile,
          // Si el usuario procesó subdivisiones usamos el cadJson procesado;
          // si no, fallback al cadJson original del parser. Ambos son válidos
          // para el flow de guardado (el service decide si lo pasa al worker
          // según el tipo).
          cadJson:          this.processedCadJson || this.cadJson,
          screenshots,
          name, code,
          type:             effectiveType,
          layers:           layersPayload,
          cadLayeredDesign: this.cadLayeredDesign,
          // electrical/trenches: pasamos el .epcb ya subido para que el
          // service no llame al worker de mecánico. Mech queda en null y el
          // service usa CadWorkerClient como siempre.
          sceneAws,
          onProgress:       (key) => this.ShowLoader({ loading: true, text: this.$t(key) }),
        })

        // Resumen para el modal de éxito (mismos números que vienen del worker).
        // Los conteos de estructura/hincas/módulos/paneles son de la subdivisión
        // MECÁNICA → en electrical/trenches no aplican y se omiten (null → el
        // modal no pinta esas líneas).
        this.$refs.refSaveSuccessModal.show({
          dxfFileName: this.uploadedFile?.name ?? null,
          structures:  design.summaryFields.includes('structures') ? this.total_structures : null,
          pilings:     design.summaryFields.includes('pilings')    ? this.totalEntitiesDesign?.pilings : null,
          modules:     design.summaryFields.includes('modules')    ? this.totalEntitiesDesign?.modules : null,
          panels:      design.summaryFields.includes('panels')     ? this.totalEntitiesDesign?.panels : null,
          screenshots: screenshots.length,
        })
        this.$emit('epc-saved', pictogram)
      } catch (err) {
        logger.error('save pictogram error:', err)
        this.$toast.open({
          message:  this.$t(err.message) || err.message || this.$t('save_error'),
          type:     'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.showPanelScreenshot = false
        this.ShowLoader({ loading: false, text: null })
      }
    },
  },

  created() {
    this.fonts = FONTS
    // Propiedades de instancia (NO reactivas) para el avance lineal.
    // Mantienen referencias a estructuras pesadas (THREE.Group, cad entity con
    // arrays de vertices) sin que Vue las observe.
    // Overlay de render de los carriles de avance (engine-adjacent). POSEE sus
    // meshes (handle → THREE.Group) fuera de Vue. Getter al DxfViewer vivo.
    this._advanceOverlay = new AdvanceOverlay(() => this.$refs.cadViewer?.GetDxfViewer?.())
    // Overlay de render del resaltado del footer (POSEE sus meshes, fuera de Vue).
    this._highlightOverlay = new HighlightOverlay(() => this.$refs.cadViewer?.GetDxfViewer?.())
    this._advanceCurrent = null                 // { handle, entity } activo en el modal
    this._inlineGesture  = null                 // { direction, deltaMeters, totalMeters } del gesto inline en curso
    this._armedKeyHandler = null                // listener Escape mientras hay avance inline armado
    this._inlineTargetStatus = null             // estado objetivo (Opción A) a pre-seleccionar en el popover
    // Estado persistido cargado desde localStorage: handle (string) → { direction, meters, totalLength, status }
    this._advanceState   = Object.create(null)
    // Controller del tooltip de hover (engine-adjacent): POSEE el raycaster y el
    // binding de eventos fuera de Vue. Le inyectamos targets + negocio + render.
    this._hoverTooltip = new HoverTooltipController({
      getViewer:  () => this.$refs.cadViewer,
      // En modo avance inline (segmento armado) no mostramos el tooltip de hover:
      // la interacción se centra en marcar el avance, sin distraer con info.
      getTargets: () => (this.inlineArmedHandle != null ? [] : this._HoverTargets()),
      resolve:    (handle, entity) => this._ResolveHoverTooltip(handle, entity),
      onShow:     (tip) => this._ShowTooltip(tip),
      onHide:     () => this._HideTooltip(),
    })
    // Controller del GESTO de avance inline (engine-adjacent, fuera de Vue). POSEE
    // su raycaster/plane/listeners; le inyectamos targets + contexto de negocio +
    // recepción del gesto. Solo captura cuando el perfil tiene inlineAdvance.
    this._inlineAdvance = new InlineAdvanceController({
      getViewer:  () => this.$refs.cadViewer,
      getTargets: () => this._HoverTargets(),
      // Solo responde al segmento ARMADO por doble-click; en el resto devuelve null
      // y el controller deja pasar el evento (click simple = info, etc.).
      getContext: (handle, entity) => {
        if (String(handle) !== this.inlineArmedHandle) return null
        return this._GetAdvanceContext(handle, entity)
      },
      onGesture:  (g) => this._OnInlineGesture(g),
      isActive:   () => !this.isDraggable && this.inlineArmedHandle != null,
    })
  },

  beforeDestroy() {
    this._UnbindHoverTooltip()
    this._inlineAdvance.unbind()
    cancelAnimationFrame(this._armedRaf)
    if (this._armedKeyHandler) window.removeEventListener('keydown', this._armedKeyHandler, true)
    this._DisposeAllAdvanceMeshes()
    this._DisposeAllHighlightMeshes()
  },
}
</script>

<style scoped>

/* El cluster de acciones arriba-derecha (upload / volver) deja hueco a la derecha
   para el botón flotante de panel (WidgetPanelToggle, 38px @ right:10px), que vive
   fuera del Toolbar por usar un SVG propio.
   NOTA: el desplazamiento al FIJAR el panel ya no necesita offsets — el chrome vive
   dentro de `.viewer-area` (flex-1), que se encoge y arrastra todo el chrome. */
.epc-toolbar--align-right :deep(.epc-toolbar-content) {
  padding-right: 48px;
}

/* Panel fijado → el WidgetPanelToggle se oculta (v-if="!pinned"); el hueco de
   48px que lo reservaba deja de tener sentido y se recupera. */
.epc-toolbar--no-toggle-gap :deep(.epc-toolbar-content) {
  padding-right: 0;
}

/* El indicador flotante de capa activa (.active-layer-pill) usa clases Tailwind
   en el template — pill claro con el azul de marca, coherente con el toolbar. */

</style>
